/**
 * Le module Comptabilité — Facture, et bientôt Recouvrement.
 *
 * Cadrage « Stafa v2 — Étape 10 : la facturation et le recouvrement », clos le
 * 09/09/2026. Maquette `docs/maquettes/facturation.html`, validée le même jour.
 *
 * Même découpe que partout : les vues dessinent, `EcranComptabilite` décide, et
 * le calcul est dans `facturation.ts`, en fonctions pures.
 *
 *
 * TROIS ÉCRANS, ET UN SEUL CHEMIN ENTRE EUX
 * ---------------------------------------------------------------------------
 * « À facturer » montre les ordres qu'on a poussés depuis l'exploitation ; le
 * bouton « Facturer » les transforme en BROUILLONS et ouvre la page d'édition,
 * qui les ENCHAÎNE une à une ; « Factures » montre tout ce qui existe.
 *
 * Le chemin ne se remonte pas : une fois validée, une pièce ne revient pas dans
 * « À facturer ». C'est la contrepartie d'une numérotation sans trou.
 *
 *
 * LE BOUTON COMPTE DES FACTURES, PAS DES ORDRES COCHÉS
 * ---------------------------------------------------------------------------
 * Demande du 09/09/2026. Deux ordres d'un client réglé « par envoi » font UNE
 * facture ; deux ordres d'un client réglé « par ordre » en font DEUX. Le
 * chiffre qu'on veut lire avant d'appuyer est le nombre de pièces à relire,
 * pas le nombre de cases cochées. La règle est dans `combienDeFactures`, et
 * l'API applique exactement la même dans `POST /factures`.
 *
 *
 * CE QUE CET ÉCRAN N'AFFICHE PAS, ET POURQUOI
 * ---------------------------------------------------------------------------
 * **L'état « Payée ».** La maquette le montre ; aucune colonne de la base ne
 * dit qu'une facture est encaissée. Le règlement est le sous-menu
 * Recouvrement, qui n'est pas construit. Afficher « Payée » aujourd'hui
 * demanderait d'inventer la donnée, donc l'écran s'arrête à quatre états.
 *
 * **Un total stocké.** L'API ne rend aucun total : elle rend, par pièce, ce que
 * pèse chaque taux de TVA. Les colonnes HT et TTC se calculent ici, par le même
 * `ventilation()` que la page d'édition. Deux chemins d'affichage, une règle.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EcranRecouvrement } from './EcranRecouvrement.js';
import { BandeauPennylane } from './BandeauPennylane.js';
import {
  changerPieceAnnexe,
  deposerLesAnnexes,
  envoyerAPennylane,
  lireAnnexesPrevues,
  type PieceAnnexe,
  lirePdfPennylane,
  ajouterUneLigne,
  annulerLaValidation,
  etablirUnAvoir,
  enregistrerLEnvoi,
  facturerLesOrdres,
  lireLesFactures,
  lireLesOrdresAFacturer,
  corrigerLesDatesTranscrites,
  declarerDejaFacturee,
  lireUneFacture,
  modifierUneFacture,
  modifierUneLigne,
  supprimerUneFacture,
  supprimerUneLigne,
  validerUneFacture,
  type EchecApi,
  type FactureOuverte,
  type LigneAAvoirer,
  type LigneFacture,
} from './api.js';
import {
  CANAUX,
  canalDit,
  combienDeFactures,
  dateDite,
  decompteAFacturer,
  decompteFactures,
  etatDit,
  etatFacture,
  grouperLignes,
  grouperParClient,
  joursDeRetard,
  jourDit,
  LIBELLE_SURCHARGE,
  montant,
  pieceDite,
  porteUneSurcharge,
  saisieBrute,
  saisieDite,
  tauxDit,
  totalLigne,
  totauxDePiece,
  trajetDit,
  ventilation,
  type GroupeClient,
  type OrdreAFacturer,
  type PieceListee,
} from './facturation.js';

/** Les taux proposés. La saisie reste libre : ce sont des raccourcis. */
const TAUX = [
  { valeur: '20', texte: '20 %' },
  { valeur: '10', texte: '10 %' },
  { valeur: '5.5', texte: '5,5 %' },
  { valeur: '0', texte: '0 % — autoliquidation' },
] as const;

/**
 * LES REFUS DE L'API, EN FRANÇAIS.
 *
 * Chaque code que `factures.ts` peut renvoyer a sa phrase ici. Sans elle,
 * l'écran afficherait « litige_ouvert » à quelqu'un qui cherche à facturer.
 */
const REFUS: Readonly<Record<EchecApi, string>> = {
  // LES SIX QUI VIENNENT VRAIMENT D'ICI.
  litige_ouvert:
    'Cet ordre porte un litige ouvert. Réglez-le d’abord — c’est la seule chose qui bloque une facture.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise:
    'Cette pièce est émise : elle ne se modifie plus. Corrigez-la par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas — elle brûlerait un numéro.',

  // LES QUATRE ORDINAIRES.
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette pièce n’existe plus. Elle a pu être effacée entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  invalide:
    'Une valeur saisie n’est pas acceptée. Vérifiez les montants, les taux et les dates.',

  // CE QUI N'ARRIVE PAS ICI. La carte reste exhaustive, comme partout : c'est
  // ce qui garantit qu'aucun refus de l'API ne s'affichera en code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se facture pas.',
  code_pris: 'Ce nom est déjà pris.',
  conflit: 'Cette période est déjà occupée.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Cet élément est encore employé ailleurs.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : elle ne se déplace plus et ne perd plus son camion.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert: 'Un litige est déjà ouvert sur cette étape.',
  litige_deja_resolu: 'Ce litige est déjà résolu.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

/** La phrase d'un refus. La carte est exhaustive : il y en a toujours une. */
function refusDit(cause: EchecApi): string {
  return REFUS[cause];
}

// ===========================================================================
// Vue — l'onglet « À facturer »
// ===========================================================================

interface ProprietesAFacturer {
  readonly groupes: readonly GroupeClient[];
  readonly retenus: ReadonlySet<string>;
  readonly replies: ReadonlySet<string>;
  readonly occupe: boolean;
  readonly quandOrdre: (id: string) => void;
  /**
   * OUVRIR la fiche de l'ordre. `undefined` quand l'écran est monté hors de
   * l'application (aperçu statique) : le numéro reste alors du texte.
   */
  readonly quandOuvrirOrdre?: ((id: string) => void) | undefined;
  readonly quandClient: (groupe: GroupeClient) => void;
  readonly quandPlier: (clientId: string) => void;
  readonly quandFacturer: () => void;
}

export function VueAFacturer(p: ProprietesAFacturer) {
  const combien = combienDeFactures(p.groupes, p.retenus);

  return (
    <>
      <div className="barre-outils">
        <div className="a-droite">
          <button
            className="bouton"
            type="button"
            disabled={combien === 0 || p.occupe}
            onClick={p.quandFacturer}
          >
            {combien === 0
              ? 'Facturer'
              : `Facturer — ${String(combien)} facture${combien > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
      <p className="decompte">{decompteAFacturer(p.groupes)}</p>

      {p.groupes.length === 0 ? (
        <div className="vide">
          <p>
            Rien n’attend de facture. Les ordres arrivent ici depuis « Ordres de
            transport », par le geste « Envoyer en facturation ».
          </p>
        </div>
      ) : (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col" className="colonne-case">
                  {' '}
                </th>
                <th scope="col">Ordre</th>
                <th scope="col">Trajet</th>
                <th scope="col">Livré le</th>
                <th scope="col">En facturation depuis</th>
                <th scope="col" className="n">
                  Vente HT
                </th>
              </tr>
            </thead>
            <tbody>
              {p.groupes.map((groupe) => {
                const plie = p.replies.has(groupe.clientId);
                const tous = groupe.ordres.every((o) => p.retenus.has(o.id));
                const aucun = groupe.ordres.every((o) => !p.retenus.has(o.id));
                return (
                  <Fragment key={groupe.clientId}>
                    <tr
                      className={
                        groupe.facturableLe === null
                          ? 'ligne-client'
                          : 'ligne-client attente-mois'
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={tous}
                          ref={(boite) => {
                            // LE TROISIÈME ÉTAT : une partie des ordres du
                            // client est cochée. Il ne s'écrit pas en JSX —
                            // `indeterminate` n'est pas un attribut HTML.
                            if (boite !== null) boite.indeterminate = !tous && !aucun;
                          }}
                          aria-label={`Retenir ${groupe.nom}`}
                          onChange={() => {
                            p.quandClient(groupe);
                          }}
                        />
                      </td>
                      <td colSpan={4}>
                        <button
                          className="plier"
                          type="button"
                          aria-expanded={!plie}
                          aria-label={`${plie ? 'Déplier' : 'Replier'} ${groupe.nom}`}
                          onClick={() => {
                            p.quandPlier(groupe.clientId);
                          }}
                        >
                          {plie ? '▸' : '▾'}
                        </button>
                        {groupe.nom}
                        <span className="compte-ordres">
                          {' '}
                          — {groupe.ordres.length} ordre
                          {groupe.ordres.length > 1 ? 's' : ''}
                        </span>
                        {groupe.facturableLe !== null && (
                          <span className="statut brouillon suit-un-nom">
                            Facturable le {jourDit(groupe.facturableLe)}
                          </span>
                        )}
                      </td>
                      <td className="n code">{montant(groupe.total)}</td>
                    </tr>
                    {!plie &&
                      groupe.ordres.map((ordre) => (
                        <tr
                          key={ordre.id}
                          className={
                            groupe.facturableLe === null ? undefined : 'attente-mois'
                          }
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={p.retenus.has(ordre.id)}
                              aria-label={`Retenir ${ordre.numero}`}
                              onChange={() => {
                                p.quandOrdre(ordre.id);
                              }}
                            />
                          </td>
                          {/* LE NUMÉRO OUVRE L'ORDRE — 14/09/2026.

                              UN BOUTON, PAS TOUTE LA RANGÉE. La rangée porte
                              déjà une case à cocher : la rendre cliquable
                              ferait qu'un clic à côté de la case ouvrirait un
                              écran au lieu de cocher. Deux gestes, deux
                              cibles distinctes.

                              LA RÉFÉRENCE CLIENT RESTE HORS DU BOUTON : elle
                              n'est pas le nom de l'ordre, et l'inclure
                              élargirait la zone cliquable à du texte qui ne
                              mène nulle part. */}
                          <td className="code">
                            {p.quandOuvrirOrdre === undefined ? (
                              ordre.numero
                            ) : (
                              <button
                                type="button"
                                className="lien-ot"
                                onClick={() => {
                                  p.quandOuvrirOrdre?.(ordre.id);
                                }}
                                title={`Ouvrir l’ordre ${ordre.numero}`}
                              >
                                {ordre.numero}
                              </button>
                            )}
                            {ordre.reference_client !== null && (
                              <span className="secondaire">
                                {ordre.reference_client}
                              </span>
                            )}
                          </td>
                          <td>{trajetDit(ordre) ?? '—'}</td>
                          <td className="code">{jourDit(ordre.jour_fin) ?? '—'}</td>
                          <td className="code">
                            {jourDit(
                              (ordre.envoye_facturation_le ?? '').slice(0, 10),
                            ) ?? '—'}
                            {ordre.envoye_facturation_par !== null &&
                              ` · ${ordre.envoye_facturation_par}`}
                          </td>
                          <td className="n code">
                            {montant(Math.round(Number(ordre.prix_vente ?? 0) * 100))}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Vue — la page d'édition, enchaînée
// ===========================================================================

/**
 * L'ÉTAT DE L'APERÇU PENNYLANE — 11/09/2026.
 *
 * `pdf` est une adresse `blob:` fabriquée par l'écran à partir des octets que
 * Stafa relaie. Pennylane interdit qu'on encadre ses pages
 * (`frame-ancestors 'self' … https://*.pennylane.com`, constaté le 11/09), donc
 * le document ne peut pas venir de chez eux : il vient de nous.
 */
export interface ApercuPennylane {
  readonly etat: 'absent' | 'en_cours' | 'affiche' | 'refus';
  /** Une adresse `blob:`, jamais celle de Pennylane. */
  readonly pdf: string | null;
  /** Le message de refus, déjà mis en mots par la table REFUS de l'écran. */
  readonly refus: string | null;
  /** Le détail rendu par l'API — souvent le texte de Pennylane lui-même. */
  readonly detail: string | null;
  /**
   * LE DÉTAIL EST-IL UNE ALERTE, ou un simple compte rendu ?
   *
   * Posé le 14/09/2026 en corrigeant un défaut : `detail` n'était DESSINÉ que
   * dans le cas d'un refus. Sur un envoi réussi, il était rempli et jamais
   * montré — « Stafa n'a pas pu retenir cette pièce » n'a donc jamais pu être
   * lu par personne, alors que c'est l'avertissement qui annonce deux
   * brouillons chez Pennylane.
   *
   * Maintenant qu'il s'affiche aussi sur un succès, il porte deux natures :
   * « 2 pièce(s) jointe(s) » est un compte rendu, « Attention : … » est une
   * alerte. Les peindre pareil effacerait la différence ; deviner d'après le
   * texte serait pire. On la porte donc explicitement.
   */
  readonly alerte: boolean;
  /** Les montants tels que PENNYLANE les a calculés. */
  readonly ht: string | null;
  readonly ttc: string | null;
}

interface ProprietesEdition {
  readonly ouverte: FactureOuverte;
  readonly apercu: ApercuPennylane;
  /** Envoie la facture chez Pennylane et affiche le document qui en revient. */
  readonly quandApercu: () => void;
  /**
   * LES PIÈCES QUI ACCOMPAGNERONT LA FACTURE — 14/09/2026.
   *
   * Vide veut dire « aucune pièce candidate », et le bloc ne se dessine pas.
   * Une pièce RETIRÉE est bien dans la liste, `partira` à faux : la faire
   * disparaître au moment où on la décoche ôterait tout moyen de la remettre.
   */
  readonly pieces: readonly PieceAnnexe[];
  readonly quandBasculerPiece: (piece: PieceAnnexe) => void;
  readonly rang: number;
  readonly combien: number;
  readonly occupe: boolean;
  readonly quandEntete: (champ: string, valeur: string) => void;
  readonly quandLigne: (ligne: LigneFacture, champ: string, valeur: string) => void;
  readonly quandRetirerLigne: (ligne: LigneFacture) => void;
  readonly quandAjouterLigne: (categorie: 'surcharge' | 'annexe') => void;
  readonly quandValider: () => void;
  readonly quandBrouillon: () => void;
  readonly quandPasser: () => void;
  readonly quandAbandonner: () => void;
  /**
   * SUPPRIMER LE BROUILLON, et rendre l'ordre à « À facturer ».
   *
   * Manque constaté par l'utilisateur le 09/09/2026 : « une facture en
   * brouillon, rien ne me permet de la supprimer et donc de remettre l'OT dans
   * À facturer ». La route existait depuis le socle, avec son test ; c'est le
   * bouton qui manquait.
   *
   * Rien n'est perdu de définitif : l'ordre revient dans la liste, l'envoi en
   * facturation n'est pas défait, et aucun numéro n'a été brûlé puisqu'un
   * brouillon n'en porte pas. Ce qui se perd, ce sont les lignes libres tapées
   * à la main — d'où la confirmation en deux temps.
   */
  readonly quandSupprimer: () => void;
  /** Le premier clic a été donné : le bouton demande confirmation. */
  readonly supprimeDemande: boolean;
  /**
   * DÉCLARER LA PIÈCE DÉJÀ FACTURÉE AILLEURS — 16/09/2026.
   *
   * Ajouté après le défaut du 15/09 : un brouillon poussé chez Pennylane y a
   * été finalisé À LA MAIN, puis supprimé dans Stafa. La facture est restée
   * numérotée et comptabilisée pendant que Stafa rendait ses seize prestations
   * à facturer.
   *
   * `null` FERME LA BOÎTE, un objet la soumet. Le refus remonte par
   * `refusDeclaration` plutôt que par le bandeau général : il se lit DANS la
   * boîte, à côté des champs à corriger.
   */
  readonly quandDeclarer: (saisie: {
    numero: string;
    datePiece: string;
    echeance: string;
    totalHt: string;
  }) => void;
  readonly refusDeclaration: string | null;
  readonly quandFermerDeclaration: () => void;
  readonly declarationOuverte: boolean;
  readonly quandOuvrirDeclaration: () => void;
}

/**
 * LA BOÎTE « DÉJÀ FACTURÉE CHEZ PENNYLANE » — 16/09/2026.
 *
 * Maquette `docs/maquettes/facture-deja-facturee.html`, validée le 16/09.
 *
 * LE TOTAL DU BROUILLON N'EST PAS AFFICHÉ ICI, et c'est la décision centrale
 * de cet écran. Le montant tapé est une DOUBLE SAISIE : l'API le compare au
 * total du brouillon et refuse s'ils diffèrent. L'afficher à côté du champ
 * détruirait le contrôle — on recopierait le chiffre au lieu d'aller le lire
 * sur la pièce. Il n'apparaît QUE dans le message de refus, qui porte les
 * deux montants.
 *
 * TROIS CHAMPS ET PAS UN DE PLUS. Stafa n'a pas l'identifiant Pennylane : il
 * est parti avec le brouillon supprimé le 15/09. Le numéro, la date et le
 * total sont tout ce qu'on peut relever sur la pièce.
 */
function BoiteDejaFacturee(p: {
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: (saisie: {
    numero: string;
    datePiece: string;
    echeance: string;
    totalHt: string;
  }) => void;
}) {
  const [numero, setNumero] = useState('');
  const [datePiece, setDatePiece] = useState('');
  const [echeance, setEcheance] = useState('');
  const [totalHt, setTotalHt] = useState('');

  /**
   * L'ÉCHÉANCE NE PEUT PAS PRÉCÉDER LA PIÈCE — la base l'exige, et l'API le
   * dit en français. On l'attrape ici EN PLUS, pour que le bouton s'éteigne
   * avant l'aller-retour plutôt qu'après.
   */
  const datesInversees = datePiece !== '' && echeance !== '' && echeance < datePiece;

  // LES QUATRE SONT OBLIGATOIRES. Le bouton reste éteint tant qu'il en manque
  // un : l'API refuserait, et un aller-retour pour dire « il manque la date »
  // est un aller-retour de trop.
  const complet =
    numero.trim() !== '' &&
    datePiece !== '' &&
    echeance !== '' &&
    totalHt.trim() !== '' &&
    !datesInversees;

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Cette facture est déjà facturée chez Pennylane</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            p.quandEnregistrer({ numero, datePiece, echeance, totalHt });
          }}
        >
          <p className="quoi-decroche">
            Stafa va l’enregistrer comme émise, avec le numéro que tu lui donnes.{' '}
            <b>Rien ne part chez Pennylane</b> : aucune création, aucune
            finalisation, aucune suppression.
          </p>

          <label className="etiquette-boite" htmlFor="declare-numero">
            Numéro de la facture
          </label>
          <input
            id="declare-numero"
            className="champ-boite"
            type="text"
            value={numero}
            disabled={p.envoi}
            onChange={(e) => setNumero(e.target.value)}
          />
          <p className="aide-boite">
            Tel qu’il est écrit sur la pièce. Stafa refuse un numéro déjà porté par
            une autre de tes factures.
          </p>

          <label className="etiquette-boite" htmlFor="declare-date">
            Date de la pièce
          </label>
          <input
            id="declare-date"
            className="champ-boite"
            type="date"
            value={datePiece}
            disabled={p.envoi}
            onChange={(e) => setDatePiece(e.target.value)}
          />
          <p className="aide-boite">
            La date portée par la pièce, pas celle d’aujourd’hui : c’est elle qui
            décide du mois de chiffre d’affaires.
          </p>

          {/* L'ÉCHÉANCE EST DEMANDÉE, ET NON CALCULÉE — 16/09/2026. Trente
              jours est NOTRE règle ; celle de la pièce est celle que le client
              a sous les yeux. La deviner, c'est relancer au mauvais moment.
              Le premier jet ne la demandait pas : FA202609457 est entrée au
              16/10 là où la vraie dit 14/10. */}
          <label className="etiquette-boite" htmlFor="declare-echeance">
            Échéance
          </label>
          <input
            id="declare-echeance"
            className="champ-boite"
            type="date"
            value={echeance}
            disabled={p.envoi}
            onChange={(e) => setEcheance(e.target.value)}
          />
          <p className="aide-boite">
            {datesInversees
              ? 'L’échéance ne peut pas précéder la date de la pièce.'
              : 'Celle qui est imprimée sur la pièce. C’est elle qui décide des relances.'}
          </p>

          <label className="etiquette-boite" htmlFor="declare-total">
            Total HT de la pièce
          </label>
          <input
            id="declare-total"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={totalHt}
            disabled={p.envoi}
            onChange={(e) => setTotalHt(e.target.value)}
          />
          <p className="aide-boite">
            <b>Relève-le sur la facture Pennylane, ne le recopie pas d’ici.</b> C’est
            la seule vérification possible : Stafa le compare à ce que porte le
            brouillon, et refuse si les deux diffèrent.
          </p>

          {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={p.envoi || !complet}>
              {p.envoi ? 'Enregistrement…' : 'Enregistrer comme émise'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.envoi}
              onClick={p.quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function VueEdition(p: ProprietesEdition) {
  const f = p.ouverte.facture;

  /**
   * L'ÉCRAN DESCEND JUSQU'AU DOCUMENT dès qu'il est prêt.
   *
   * DÉFAUT CONSTATÉ LE 11/09, chez l'utilisateur : « ça me dit Pennylane
   * prépare la facture puis rien ne se passe ». Le volet était pourtant bien
   * là — vérifié dans le DOM. Mais `.fenetre-facture` porte `max-height: 92vh`
   * et `overflow-y: auto` : le volet, ajouté SOUS les boutons, apparaissait
   * sept cents pixels plus bas, hors de vue. Rien ne disait qu'il fallait
   * faire défiler.
   *
   * UN APERÇU QU'IL FAUT ALLER CHERCHER N'EST PAS UN APERÇU.
   *
   * POURQUOI CE DÉFAUT A SURVÉCU AU RENDU : `apercu.tsx` dessinait l'éditeur
   * NU, sans la fenêtre qui l'encadre dans l'application. Rien ne le coupait,
   * donc rien ne se voyait. L'aperçu a été corrigé en même temps.
   *
   * `block: 'start'`, ET NON `'nearest'`. Mesuré le 11/09 : avec `'nearest'`
   * la fenêtre ne bougeait que du strict minimum — le haut du volet arrivait
   * à trois pixels du bas. Techniquement « visible », en pratique invisible.
   * Un correctif qui satisfait la mesure sans satisfaire l'œil n'est pas un
   * correctif.
   *
   * CE QUE ÇA COÛTE : les boutons sortent de la vue. C'est assumé — le geste
   * qu'on vient de faire était « montre-moi le document », pas « garde le
   * bouton Valider sous la main ». Il revient d'un coup de molette.
   */
  /**
   * LE MENU SE FERME AU CLIC DEHORS ET À ÉCHAP — 12/09/2026.
   *
   * `details` ne le fait pas tout seul : CONSTATÉ À L'ESSAI, pas supposé. Sans
   * ces quelques lignes, le menu reste ouvert par-dessus la facture dès qu'on
   * clique ailleurs, et Échap ne le referme pas.
   *
   * `mousedown` ET NON `click` : un clic dont le bouton s'enfonce dehors et se
   * relâche dedans ne doit pas compter comme un clic dedans.
   *
   * LES DEUX ÉCOUTEURS NE VIVENT QUE PENDANT L'OUVERTURE. Posés en permanence,
   * ils tourneraient à chaque clic de l'application pour ne rien faire.
   */
  const menu = useRef<HTMLDetailsElement | null>(null);
  const [menuOuvert, setMenuOuvert] = useState(false);
  useEffect(() => {
    if (!menuOuvert) return undefined;
    const dehors = (e: MouseEvent) => {
      if (menu.current !== null && !menu.current.contains(e.target as Node)) {
        menu.current.open = false;
        setMenuOuvert(false);
      }
    };
    const echap = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menu.current !== null) {
        menu.current.open = false;
        setMenuOuvert(false);
      }
    };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => {
      document.removeEventListener('mousedown', dehors);
      document.removeEventListener('keydown', echap);
    };
  }, [menuOuvert]);

  const volet = useRef<HTMLDivElement | null>(null);
  const etatApercu = p.apercu.etat;
  useEffect(() => {
    if (etatApercu !== 'affiche' && etatApercu !== 'refus') return;
    /**
     * ON NE DESCEND QUE SI LES VOLETS SONT EMPILÉS — 14/09/2026.
     *
     * Depuis que le document est À DROITE, il est déjà sous les yeux : faire
     * défiler la fenêtre ferait disparaître les lignes qu'on est en train de
     * comparer avec lui, c'est-à-dire l'inverse de ce qu'on vient de gagner.
     *
     * Sous 1100 px, les volets repassent l'un sous l'autre et le défaut du
     * 11/09 revient tel quel — « ça me dit Pennylane prépare la facture puis
     * rien ne se passe ». Le défilement sert alors, et seulement là.
     *
     * LA LARGEUR EST LUE AU MOMENT DU BESOIN, et non retenue dans un état :
     * elle ne change qu'au redimensionnement de la fenêtre, et un état de plus
     * serait un état de plus à tenir juste.
     */
    const empiles = window.matchMedia('(max-width: 1100px)').matches;
    if (empiles) {
      volet.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [etatApercu]);
  const pied = ventilation(p.ouverte.lignes, f.remise_globale);

  /**
   * LA REMISE NE S'AFFICHE QUE SI ELLE SERT — 12/09/2026.
   *
   * POURQUOI LA MASQUER. L'envoi chez Pennylane REFUSE toute remise, de ligne
   * comme de pied : on montrait donc un champ qui, rempli, fait échouer la
   * facture. Une colonne entière de zéros, sur huit colonnes, pour un piège.
   *
   * POURQUOI PAS TOUJOURS. Si une pièce en porte une — une facture d'avant ce
   * jour, ou une reprise —, la cacher rendrait le TOTAL AFFICHÉ IMPOSSIBLE À
   * RETROUVER : on verrait 968,69 sous des lignes qui font 1 000,00, sans rien
   * qui l'explique. Un chiffre qu'on ne peut pas refaire de tête est pire qu'une
   * colonne de trop.
   *
   * Elle reparaîtra pour tout le monde le jour où la remise sera éprouvée chez
   * eux — c'est la même dette, pas une nouvelle.
   */
  const porteUneRemise =
    (f.remise_globale !== null && Number(f.remise_globale) !== 0) ||
    p.ouverte.lignes.some((l) => l.remise !== null && Number(l.remise) !== 0);
  const colonnes = porteUneRemise ? 8 : 7;
  const adresse = [f.client_code_postal, f.client_ville]
    .filter((m) => m !== null)
    .join(' ');

  return (
    <>
      {/*
        LE CLIENT PASSE EN PREMIER — direction B, validée le 12/09/2026.

        Le bandeau disait « Facture 1 sur 3 — DERET TRANSPORTEUR ». Quand on
        enchaîne cinq factures, ce qu'on cherche c'est DE QUI il s'agit ; le
        rang, on l'a déjà sous les yeux dans les pastilles à droite.

        L'ADRESSE REMONTE ICI AUSSI, ce qui libère une barre entière dans
        l'en-tête. Elle ne se saisit pas — elle est figée sur la pièce depuis sa
        création — donc elle n'a rien à faire dans un bloc de formulaire.
      */}
      <div className="bandeau-suite">
        <b>{f.client_nom}</b>
        <span className="maigre">
          Brouillon · facture {p.rang} sur {p.combien}
        </span>
        <span className="maigre bandeau-adresse">
          {[f.client_adresse, adresse]
            .filter((m) => m !== '' && m !== null)
            .join(' · ')}
        </span>
        <span
          className="suite-points"
          aria-label={`Facture ${String(p.rang)} sur ${String(p.combien)}`}
        >
          {Array.from({ length: p.combien }, (_, i) => (
            <span
              key={i}
              className={
                i + 1 < p.rang
                  ? 'suite-point fait'
                  : i + 1 === p.rang
                    ? 'suite-point ici'
                    : 'suite-point'
              }
            />
          ))}
        </span>
      </div>

      <div className="entete-facture">
        {/*
          « RÉFÉRENCE À RAPPELER » A DISPARU DE L'EN-TÊTE — décision 2 de la
          maquette du 11/09/2026.

          Le modèle de facture n'imprime AUCUNE référence globale : il en
          imprime une PAR LIGNE, sous le numéro d'OT. Ce champ était donc un
          champ qu'on remplissait pour rien, et qui donnait à croire que la
          référence saisie ressortirait sur le document.

          LA COLONNE `factures.reference_client` RESTE EN BASE, et la liste des
          factures continue de l'afficher pour les pièces anciennes. Effacer
          une colonne est irréversible, la masquer ne l'est pas — et c'est elle
          qui a servi à garnir les lignes des factures déjà existantes
          (migration 20260911060000).
        */}
        <div>
          <label htmlFor="f-date">Date de facture</label>
          <input
            id="f-date"
            type="date"
            defaultValue={f.date_piece}
            onBlur={(e) => {
              p.quandEntete('date_piece', e.target.value);
            }}
          />
        </div>
        <div>
          <label htmlFor="f-ech">Échéance</label>
          <input
            id="f-ech"
            type="date"
            defaultValue={f.echeance ?? ''}
            onBlur={(e) => {
              p.quandEntete('echeance', e.target.value);
            }}
          />
          <div className="secondaire">30 jours — modifiable</div>
        </div>
      </div>

      {/*
        CE QUI DÉFILE, ET CE QUI NE DÉFILE PLUS — direction B, 12/09/2026.
        Tout ce qui est DANS ce conteneur défile : les lignes, et le document
        Pennylane quand il est demandé. Tout ce qui est en dehors — le bandeau,
        les dates, et surtout LE PIED — reste à l'écran.

        POURQUOI ÇA COMPTE : on validait sans voir le total. Mesuré le 12/09,
        l'éditeur faisait 1 175 px pour une fenêtre de 828 : avec quatre lignes,
        les totaux et le bouton « Valider » étaient hors de vue au moment même
        où l'on cliquait sur un geste IRRÉVERSIBLE.
      */}
      {/*
        DEUX VOLETS CÔTE À CÔTE — maquette du 14/09/2026, validée.

        À GAUCHE ce que Stafa tient et qu'on peut corriger ; À DROITE le
        document que le client recevra. Avant, le document était DESSOUS, dans
        un cadre de 620 px : pour comparer un montant imprimé avec la ligne qui
        l'a produit, il fallait défiler, retenir, remonter. On vérifie mal ce
        qu'on ne voit pas en même temps — et le clic suivant brûle un numéro
        définitif.

        SOUS 1100 px ILS SE REMETTENT L'UN SOUS L'AUTRE, comme avant. Sur un
        portable étroit, rien n'est perdu ; rien n'est gagné non plus.
      */}
      <div className="corps-facture deux-volets">
        <div className="volet-saisie">
        {/*
        LE TABLEAU EST GROUPÉ COMME LE DOCUMENT — maquette du 11/09/2026,
        « comme sur le doc ». Deux groupes, chacun fermé par son sous-total et
        suivi de son bouton d'ajout.

        LE GROUPE EST UNE LIGNE DE TABLEAU, pas un tableau par groupe : deux
        tableaux côte à côte alignent mal leurs colonnes, et l'œil doit pouvoir
        descendre une colonne de prix d'un bout à l'autre.

        LE SOUS-TOTAL FERME LE GROUPE, il ne l'ouvre pas : un total placé avant
        ses lignes se lit comme un objectif, pas comme un résultat.
      */}
        <div className="tableau-cadre cadre-lignes">
          <table>
            <thead>
              <tr>
                <th scope="col" className="colonne-designation">
                  Désignation
                </th>
                <th scope="col">Réf. client</th>
                <th scope="col" className="n">
                  Qté
                </th>
                <th scope="col" className="n">
                  PU HT
                </th>
                {porteUneRemise && (
                  <th scope="col" className="n">
                    Remise
                  </th>
                )}
                <th scope="col">TVA</th>
                <th scope="col" className="n">
                  Total HT
                </th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {grouperLignes(p.ouverte.lignes).map((groupe) => (
                <Fragment key={groupe.clef}>
                  <tr className="groupe-edition">
                    <td colSpan={colonnes}>{groupe.titre}</td>
                  </tr>

                  {groupe.lignes.length === 0 ? (
                    <tr className="vide-groupe">
                      <td colSpan={colonnes}>
                        {groupe.clef === 'annexes'
                          ? 'Aucune prestation annexe — ce groupe ne s’imprimera pas.'
                          : 'Aucun transport sur cette facture.'}
                      </td>
                    </tr>
                  ) : (
                    groupe.lignes.map((ligne) => (
                      <tr
                        key={ligne.id}
                        className={
                          ligne.categorie === 'surcharge' ? 'ligne-surcharge' : ''
                        }
                      >
                        <td>
                          {ligne.categorie === 'surcharge' ? (
                            <>
                              {/*
                              LE LIBELLÉ EST FIGÉ — décision du 10/09. Il n'est
                              pas saisissable : l'exploitant ne pose que le
                              montant. Figé ne veut pas dire gravé : il est
                              écrit SUR la ligne à sa création, donc une
                              facture déjà émise gardera le sien si le libellé
                              change un jour.
                            */}
                              <span className="quoi-surcharge">
                                {ligne.designation}
                              </span>
                              <span className="secondaire">
                                Une seule par facture — montant saisi
                              </span>
                            </>
                          ) : ligne.ot_id === null ? (
                            <span className="saisie-ligne">
                              <input
                                className="a-gauche"
                                defaultValue={ligne.designation}
                                aria-label="Désignation"
                                onBlur={(e) => {
                                  p.quandLigne(ligne, 'designation', e.target.value);
                                }}
                              />
                              {/* UNE PRESTATION ANNEXE PORTE SON EXPLICATION,
                                  et la maquette le montre : un intitulé, puis
                                  « 1 h de franchise dépassée — attente quai
                                  Carreras Ingré ». Elle n'en avait pas au
                                  premier rendu du 14/09 — constaté à l'écran,
                                  pas relu dans le fichier. */}
                              <textarea
                                className="champ saisie-detail"
                                defaultValue={ligne.detail ?? ''}
                                rows={2}
                                aria-label={`Détail imprimé de ${ligne.designation}`}
                                placeholder="Explication imprimée sous l’intitulé"
                                onBlur={(e) => {
                                  if (e.target.value !== (ligne.detail ?? '')) {
                                    p.quandLigne(ligne, 'detail', e.target.value);
                                  }
                                }}
                              />
                            </span>
                          ) : (
                            <>
                              {ligne.designation}
                              {/* LE DÉTAIL, TEL QU'IL S'IMPRIMERA — cadrage du
                                  14/09. Il porte la date, les références,
                                  l'enlèvement, la livraison et la charge, et
                                  il est CORRIGEABLE : un client qui demande
                                  une formulation particulière ne doit pas
                                  passer par le développement.

                                  UN `textarea` ET NON UN `input` : le texte
                                  porte de vrais retours à la ligne, et un
                                  champ d'une seule ligne les avalerait
                                  silencieusement à la première correction. */}
                              <textarea
                                className="champ saisie-detail"
                                defaultValue={ligne.detail ?? ''}
                                rows={4}
                                aria-label={`Détail imprimé de ${ligne.designation}`}
                                placeholder="Aucun détail — il s’imprimera sans"
                                onBlur={(e) => {
                                  if (e.target.value !== (ligne.detail ?? '')) {
                                    p.quandLigne(ligne, 'detail', e.target.value);
                                  }
                                }}
                              />
                            </>
                          )}
                        </td>
                        <td className="saisie-ligne">
                          {/*
                          UNE SURCHARGE NE SE RATTACHE À AUCUN TRANSPORT : elle
                          vaut pour toute la facture, donc elle ne porte pas de
                          référence. La base le refuse ; l'écran ne propose même
                          pas le champ.
                        */}
                          {ligne.categorie === 'surcharge' ? (
                            <span className="ref-fige">—</span>
                          ) : (
                            <input
                              defaultValue={ligne.reference_client ?? ''}
                              aria-label="Référence client"
                              onBlur={(e) => {
                                p.quandLigne(ligne, 'reference_client', e.target.value);
                              }}
                            />
                          )}
                        </td>
                        <td className="n saisie-ligne">
                          <input
                            defaultValue={saisieDite(ligne.quantite, false)}
                            aria-label="Quantité"
                            onBlur={(e) => {
                              p.quandLigne(
                                ligne,
                                'quantite',
                                saisieBrute(e.target.value),
                              );
                            }}
                          />
                        </td>
                        <td className="n saisie-ligne">
                          <input
                            defaultValue={saisieDite(ligne.prix_unitaire)}
                            aria-label="Prix unitaire"
                            onBlur={(e) => {
                              p.quandLigne(
                                ligne,
                                'prix_unitaire',
                                saisieBrute(e.target.value),
                              );
                            }}
                          />
                        </td>
                        {porteUneRemise && (
                          <td className="n saisie-ligne">
                            <input
                              defaultValue={saisieDite(ligne.remise ?? '0', false)}
                              aria-label="Remise"
                              onBlur={(e) => {
                                p.quandLigne(
                                  ligne,
                                  'remise',
                                  saisieBrute(e.target.value),
                                );
                              }}
                            />
                          </td>
                        )}
                        <td className="saisie-ligne">
                          <select
                            value={String(Number(ligne.taux_tva))}
                            aria-label="Taux de TVA"
                            onChange={(e) => {
                              p.quandLigne(ligne, 'taux_tva', e.target.value);
                            }}
                          >
                            {TAUX.map((t) => (
                              <option key={t.valeur} value={String(Number(t.valeur))}>
                                {t.texte}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="n code">{montant(totalLigne(ligne))}</td>
                        <td>
                          <button
                            className="croix"
                            type="button"
                            aria-label={`Retirer ${ligne.designation}`}
                            disabled={p.occupe}
                            onClick={() => {
                              p.quandRetirerLigne(ligne);
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}

                  {groupe.lignes.length > 0 && (
                    <tr className="sous-total-edition">
                      <td colSpan={6}>{groupe.sousTotalDit}</td>
                      <td className="n code">{montant(groupe.total)}</td>
                      <td> </td>
                    </tr>
                  )}

                  <tr className="ajout">
                    <td colSpan={colonnes}>
                      {groupe.clef === 'description' ? (
                        <>
                          {/*
                          PAS DE BOUTON « TRANSPORT » : un transport n'entre
                          que par un ordre — décision du 10/09. C'est ce qui
                          garantit qu'aucun chiffre d'affaires transport
                          n'existe sans exploitation en face.
                        */}
                          <button
                            className="bouton bouton-discret"
                            type="button"
                            disabled={p.occupe || porteUneSurcharge(p.ouverte.lignes)}
                            onClick={() => {
                              p.quandAjouterLigne('surcharge');
                            }}
                          >
                            + Surcharge gasoil
                          </button>
                          {porteUneSurcharge(p.ouverte.lignes) && (
                            <span className="secondaire suit-un-nom">
                              Déjà posée — une seule par facture
                            </span>
                          )}
                        </>
                      ) : (
                        <button
                          className="bouton bouton-discret"
                          type="button"
                          disabled={p.occupe}
                          onClick={() => {
                            p.quandAjouterLigne('annexe');
                          }}
                        >
                          + Prestation annexe
                        </button>
                      )}
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/**
          LES PIÈCES QUI PARTIRONT — posé le 14/09/2026, après le premier essai
          réel. Le volet annonçait « 4 pièce(s) jointe(s) » et Mohamed l'a dit
          tout de suite : « impossible de savoir quoi, de décocher cocher ».

          IL EST AU-DESSUS DU VOLET, donc AVANT le bouton d'aperçu dans l'ordre
          de lecture : décocher après coup ne servirait à rien, les pièces
          seraient déjà chez Pennylane.

          IL NE SE DESSINE PAS QUAND IL N'Y A RIEN. Un bloc vide « aucune
          pièce » sur chaque facture sans document serait du bruit sur la
          majorité d'entre elles ; le volet le dit déjà après l'envoi, au
          moment où quelqu'un s'attendait à en voir.
        */}
        {p.pieces.length > 0 && (
          <div className="pieces-facture">
            <div className="titre-pieces">
              Pièces qui partiront chez Pennylane avec cette facture
              <span className="maigre">
                {p.pieces.filter((piece) => piece.partira).length} sur{' '}
                {p.pieces.length}
              </span>
            </div>
            <ul className="liste-pieces">
              {p.pieces.map((piece) => (
                <li key={piece.id}>
                  <label className={piece.partira ? undefined : 'retiree'}>
                    <input
                      type="checkbox"
                      checked={piece.partira}
                      disabled={p.occupe}
                      onChange={() => p.quandBasculerPiece(piece)}
                    />
                    <span className="nom-piece" title={piece.nom}>
                      {piece.nom}
                    </span>
                    {piece.type !== null && (
                      <span className="maigre">{piece.type}</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
            {/* LE CHOIX TIENT, et il faut le dire : sans cette phrase, on
                croirait que décocher ne vaut que pour ce clic. */}
            <p className="aide-saisie">
              Décocher retire la pièce de cette facture, et ça reste vrai au
              prochain aperçu. Pour arrêter tout un type de pièce d’un coup,
              c’est dans Paramètres → Types de document.
            </p>
          </div>
        )}

        </div>

        {/*
          LE VOLET DE DROITE — LE DOCUMENT.

          IL EXISTE TOUJOURS, même vide, et c'est la différence avec l'ancien
          comportement. Un volet qui apparaît et disparaît ferait sauter la
          largeur du volet de gauche à chaque aperçu, et on perdrait la ligne
          qu'on était en train de lire.

          RIEN N'EST DEMANDÉ À PENNYLANE À L'OUVERTURE. Le document vient de
          chez eux : le réclamer d'office créerait un brouillon pour chaque
          facture seulement REGARDÉE, et un second envoi supprimerait le
          précédent. C'est le bouton qui décide, pas l'affichage.
        */}
        <div className="volet-document" ref={volet}>
          {p.apercu.etat === 'absent' && (
            <div className="document-absent">
              <p>Le document de Pennylane s’affichera ici.</p>
              <p className="aide-saisie">
                Cliquez sur « Aperçu Pennylane » en bas de la fenêtre. La
                facture part chez eux en brouillon — aucun numéro n’est pris.
              </p>
            </div>
          )}
          {p.apercu.etat !== 'absent' && (
          <div className="volet-pennylane">
            <div className="barre-pennylane">
              <b>Document Pennylane</b>
              {p.apercu.etat === 'affiche' && p.apercu.ht !== null && (
                <span className="maigre">
                  {/* LES MONTANTS DE PENNYLANE, ET NON LES NÔTRES. S'ils
                    diffèrent de ceux du pied, c'est LEUR document qui part au
                    client — il faut le voir, pas le masquer. */}
                  {p.apercu.ht} HT · {p.apercu.ttc} TTC — calculés par eux
                </span>
              )}
              <span className="a-droite">
                <span className="maigre">Brouillon — aucun numéro n’est pris</span>
              </span>
            </div>

            {p.apercu.etat === 'en_cours' && (
              <p className="attente">
                La facture part chez Pennylane, qui fabrique le document…
              </p>
            )}

            {p.apercu.etat === 'refus' && (
              <div className="refus-pennylane">
                <p className="bandeau bandeau-refus">{p.apercu.refus}</p>
                {/* LE TEXTE DE PENNYLANE, TEL QUEL. C'est la seule source qui
                  dise ce qui ne va pas ; le reformuler ferait perdre
                  l'information. */}
                {p.apercu.detail !== null && (
                  <p className="detail-pennylane">{p.apercu.detail}</p>
                )}
              </div>
            )}

            {/**
              CE QUI S'EST PASSÉ, SUR UN ENVOI RÉUSSI — posé le 14/09/2026.

              DÉFAUT CORRIGÉ ICI : `detail` n'était dessiné que dans le bloc
              `refus`. Sur un succès il était rempli et JAMAIS MONTRÉ. Deux
              choses en sont mortes sans que personne ne s'en aperçoive :
              l'avertissement « Stafa n'a pas pu retenir cette pièce », qui
              annonce deux brouillons chez Pennylane, et le compte rendu des
              pièces jointes ajouté le même jour.

              Un message écrit et non affiché est pire qu'un message absent :
              le code donne l'impression que la personne a été prévenue.
            */}
            {p.apercu.etat === 'affiche' && p.apercu.detail !== null && (
              <p
                className={
                  p.apercu.alerte ? 'bandeau bandeau-refus' : 'bandeau bandeau-fait'
                }
                role="status"
              >
                {p.apercu.detail}
              </p>
            )}

            {p.apercu.etat === 'affiche' && p.apercu.pdf !== null && (
              <iframe
                className="cadre-pennylane"
                src={p.apercu.pdf}
                title="Document Pennylane"
              />
            )}
          </div>
          )}
        </div>
      </div>

      {/*
        LE PIED NE DÉFILE PLUS — direction B, validée le 12/09/2026.

        Il est HORS de `corps-facture`, donc toujours à l'écran : le Total TTC
        et « Valider et facturer » restent sous les yeux quelle que soit la
        longueur de la facture. Mesuré le 12/09 : l'éditeur faisait 1 175 px
        pour une fenêtre de 828 — on validait sans voir le total, sur un geste
        IRRÉVERSIBLE depuis le lot 50.
      */}
      <div className="actions-bas">
        {/*
          « VALIDER ET FACTURER », ET NON PLUS « VALIDER ». Le mot dit ce qui se
          passe : la pièce part chez Pennylane et prend un numéro DÉFINITIF.
          « Valider » laissait croire à une confirmation de saisie.

          IL EST LE SEUL PLEIN, et c'est tout le propos de cette direction :
          « Valider » brûle un numéro et ne se défait pas, « Passer cette
          facture » ne fait rien du tout. Les poser côte à côte, de même taille,
          à trois centimètres l'un de l'autre, c'était organiser la faute.
        */}
        <button
          className="bouton"
          type="button"
          disabled={p.occupe || p.ouverte.lignes.length === 0}
          onClick={p.quandValider}
        >
          Valider et facturer
        </button>
        {/* L'APERÇU EST LE DOCUMENT DE PENNYLANE, pas un dessin de Stafa.
            Dessiner la facture ici ferait DEUX moteurs qui dérivent, et on
            validerait une pièce sur une image fausse. Décision du 11/09.

            IL ENVOIE LA FACTURE CHEZ EUX, et ce n'est pas un effet de bord
            caché : c'est la seule façon d'obtenir leur document. Le bouton le
            dit — « Aperçu Pennylane », pas « Aperçu ». */}
        <button
          className="bouton bouton-discret"
          type="button"
          disabled={
            p.occupe || p.apercu.etat === 'en_cours' || p.ouverte.lignes.length === 0
          }
          onClick={p.quandApercu}
        >
          {p.apercu.etat === 'en_cours' ? 'Pennylane prépare…' : 'Aperçu Pennylane'}
        </button>
        {/*
          LES GESTES RARES ENTRENT DANS UN MENU. Enregistrer, Passer, Tout
          abandonner, Supprimer : quatre boutons qui servent quelques fois par
          mois, posés jusqu'ici à côté de celui qui brûle un numéro. Les enfouir
          coûte un clic de plus rarement, et retire l'irréversible du chemin de
          la main.

          `details`/`summary` POUR L'OUVERTURE ET LE CLAVIER : le navigateur
          gère l'état ouvert/fermé, la touche Entrée et l'annonce aux lecteurs
          d'écran.

          MAIS PAS POUR LA FERMETURE. J'avais écrit ici que le navigateur
          fermait « au clic dehors et à Échap ». C'ÉTAIT FAUX, et l'essai l'a
          montré : `details` ne fait NI l'un NI l'autre. Un menu qui reste
          ouvert quand on clique ailleurs se retrouve par-dessus la facture
          qu'on essaie de lire.

          Les deux comportements sont donc écrits à la main, ci-dessus.
        */}
        <details
          className="menu-actions"
          ref={menu}
          onToggle={(e) => {
            setMenuOuvert(e.currentTarget.open);
          }}
        >
          <summary
            className="bouton bouton-discret"
            aria-label="Autres actions sur cette facture"
          >
            ⋯
          </summary>
          <div className="menu-liste">
            <button
              className="menu-choix"
              type="button"
              disabled={p.occupe}
              onClick={p.quandBrouillon}
            >
              Enregistrer en brouillon
            </button>
            <button
              className="menu-choix"
              type="button"
              disabled={p.occupe}
              onClick={p.quandPasser}
            >
              Passer cette facture
            </button>
            <button
              className="menu-choix"
              type="button"
              disabled={p.occupe}
              onClick={p.quandAbandonner}
            >
              Tout abandonner
            </button>
            {/* DÉJÀ FACTURÉE AILLEURS — 16/09/2026. Dans le menu et non dans
                la barre : c'est une RÉPARATION, elle sert quelques fois par an,
                et elle ne doit pas se cliquer par habitude à côté de
                « Valider et facturer ». */}
            <button
              className="menu-choix"
              type="button"
              disabled={p.occupe || p.ouverte.lignes.length === 0}
              onClick={p.quandOuvrirDeclaration}
            >
              Déjà facturée chez Pennylane…
            </button>
            {/* LA SUPPRESSION DEMANDE TOUJOURS DEUX CLICS. Le menu la met à
                l'abri de la main, il ne la rend pas anodine : les lignes libres
                tapées à la main sont perdues sans retour. */}
            <button
              className="menu-choix peril"
              type="button"
              disabled={p.occupe}
              onClick={p.quandSupprimer}
            >
              {p.supprimeDemande
                ? 'Confirmer la suppression'
                : 'Supprimer ce brouillon'}
            </button>
          </div>
        </details>

        {/* LES TOTAUX SONT DANS LE PIED, à droite des actions — et nulle part
            ailleurs. Les avoir à deux endroits, c'était deux chiffres à tenir
            d'accord : le premier qui dériverait ne se verrait pas. */}
        <div className="totaux">
          <div className="ligne-total">
            <span>Total HT</span>
            <span className="code">{montant(pied.totalHt)}</span>
          </div>
          {porteUneRemise && (
            <>
              <div className="ligne-total remise-globale">
                <label htmlFor="f-remise">Remise globale</label>
                <span className="saisie-remise">
                  <input
                    id="f-remise"
                    defaultValue={saisieDite(f.remise_globale ?? '0', false)}
                    onBlur={(e) => {
                      p.quandEntete('remise_globale', saisieBrute(e.target.value));
                    }}
                  />{' '}
                  %<span className="code montant-remise">−{montant(pied.remise)}</span>
                </span>
              </div>
              <div className="ligne-total">
                <span>Total HT après remise</span>
                <span className="code">{montant(pied.htApresRemise)}</span>
              </div>
            </>
          )}
          {/* UNE LIGNE DE TVA PAR TAUX. Une facture qui porte du 20 % et du 0 %
              en autoliquidation en montre deux — un total unique cacherait la
              ventilation, qui est précisément ce qui se déclare. */}
          {pied.parts.map((part) => (
            <div className="ligne-total tva" key={part.taux}>
              <span>{tauxDit(part.taux)}</span>
              <span className="code">{montant(part.tva)}</span>
            </div>
          ))}
          <div className="ligne-total gros">
            <span>Total TTC</span>
            <span className="code">{montant(pied.ttc)}</span>
          </div>
        </div>
      </div>

      {/* LA BOÎTE EST LA DERNIÈRE DU RENDU, par-dessus tout le reste : son
          voile est en `position: fixed`, et le volet d'aperçu passerait devant
          s'il venait après. */}
      {p.declarationOuverte && (
        <BoiteDejaFacturee
          envoi={p.occupe}
          refus={p.refusDeclaration}
          quandFermer={p.quandFermerDeclaration}
          quandEnregistrer={p.quandDeclarer}
        />
      )}
    </>
  );
}

// ===========================================================================
// Vue — établir un avoir. 11/09/2026.
// ===========================================================================

/**
 * CE QUE L'ÉCRAN D'AVOIR MONTRE, ET POURQUOI IL EST SÉPARÉ.
 *
 * `VueAvoir` ne sait rien charger ni écrire : elle dessine, elle rapporte les
 * gestes. C'est la coupure qui lui permet d'entrer dans `apercu.tsx` — et « un
 * écran qu'on ne rend pas est un écran que personne ne relit ». Le volet
 * Pennylane du 11/09 a fait deux pixels de haut pendant une journée parce que
 * l'aperçu le dessinait autrement que l'application.
 */
export interface ChoixAvoir {
  /** Retenue pour l'avoir. Toutes cochées à l'ouverture : le cas courant. */
  readonly prise: boolean;
  /**
   * LE MONTANT À RENDRE, POSITIF, tel qu'il est tapé. Une CHAÎNE et non un
   * nombre : le champ doit pouvoir être vide ou à demi tapé pendant la saisie,
   * ce qu'un `number` ne sait pas représenter sans inventer un zéro.
   */
  readonly prix: string;
}

interface ProprietesAvoir {
  readonly piece: PieceListee;
  readonly lignes: readonly LigneFacture[];
  readonly choix: Readonly<Record<string, ChoixAvoir>>;
  readonly motif: string;
  readonly occupe: boolean;
  /** Chargement des lignes de la facture, avant que l'écran puisse servir. */
  readonly chargement: boolean;
  readonly refus: string | null;
  readonly detail: string | null;
  readonly quandMotif: (v: string) => void;
  readonly quandPrise: (ligneId: string, prise: boolean) => void;
  readonly quandPrix: (ligneId: string, prix: string) => void;
  readonly quandEtablir: () => void;
  readonly quandFermer: () => void;
}

export function VueAvoir(p: ProprietesAvoir) {
  /**
   * LE TOTAL DE CE QU'ON VA RENDRE, calculé ici et affiché en gros.
   *
   * Sans lui, on clique sur un bouton irréversible sans savoir combien on rend.
   * L'API a bien une garde — « on n'avoire pas plus qu'on n'a facturé » — mais
   * une garde n'est pas un affichage : elle empêche la faute, elle ne la montre
   * pas avant qu'on la commette.
   */
  const total = p.lignes.reduce((somme, l) => {
    const c = p.choix[l.id];
    if (c === undefined || !c.prise) return somme;
    const prix = Number(saisieBrute(c.prix));
    if (!Number.isFinite(prix)) return somme;
    return somme + Number(l.quantite) * prix;
  }, 0);

  const retenues = p.lignes.filter((l) => p.choix[l.id]?.prise === true).length;
  const toutesPrises = retenues === p.lignes.length && p.lignes.length > 0;
  const motifVide = p.motif.trim() === '';

  return (
    <>
      <div className="bandeau-suite">
        <b>
          Établir un avoir sur {p.piece.type === 'avoir' ? 'l’avoir' : 'la facture'}{' '}
          {p.piece.numero ?? '—'}
        </b>
        <span className="maigre">
          {p.piece.client_nom}
          {/* LE MOT « TOTAL » OU « PARTIEL » EST DIT, pas déduit par la
              personne du nombre de cases cochées. C'est la distinction qui
              change ce qui reste dû au client. */}
          {' · '}
          {toutesPrises
            ? 'avoir total'
            : `avoir partiel — ${String(retenues)} ligne(s)`}
        </span>
      </div>

      {p.chargement ? (
        <p className="maigre">Lecture de la facture…</p>
      ) : (
        <>
          <div className="cadre-lignes">
            <table className="tableau-cadre">
              <thead>
                <tr>
                  <th className="colonne-designation">Ligne</th>
                  <th className="n">Qté</th>
                  <th className="n">Facturé HT</th>
                  <th className="n">À rendre HT</th>
                </tr>
              </thead>
              <tbody>
                {p.lignes.map((l) => {
                  const c = p.choix[l.id];
                  const prise = c?.prise === true;
                  return (
                    <tr key={l.id}>
                      <td className="colonne-designation">
                        <label>
                          <input
                            type="checkbox"
                            checked={prise}
                            disabled={p.occupe}
                            onChange={(e) => {
                              p.quandPrise(l.id, e.target.checked);
                            }}
                          />{' '}
                          {l.designation}
                        </label>
                        {l.reference_client !== null && (
                          <span className="secondaire">Réf. {l.reference_client}</span>
                        )}
                      </td>
                      {/* LE MÊME FORMATEUR QUE PARTOUT AILLEURS. Vu au rendu :
                          ces deux colonnes sortaient « 270.00 » avec un point,
                          juste au-dessus d'un total en « −968,69 ». Deux
                          écritures du même nombre dans le même cadre font
                          douter du chiffre, et c'est un écran où l'on rend de
                          l'argent. */}
                      <td className="n code">{saisieDite(l.quantite, false)}</td>
                      <td className="n code maigre">{saisieDite(l.prix_unitaire)}</td>
                      <td className="n">
                        {/* LE MONTANT SE SAISIT POSITIF. Le signe est posé par
                            l'API, jamais ici : deux endroits qui décident d'un
                            signe, c'est un avoir faux tôt ou tard, et un avoir
                            faux ne se corrige que par un autre avoir. */}
                        <input
                          className="saisie-ligne n"
                          type="text"
                          inputMode="decimal"
                          value={c?.prix ?? ''}
                          disabled={p.occupe || !prise}
                          aria-label={`Montant à rendre sur ${l.designation}`}
                          onChange={(e) => {
                            p.quandPrix(l.id, e.target.value);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* `pied-facture` CALE LE BLOC À DROITE — même règle que le pied de
              l'éditeur. Vu au rendu : sans lui, la barre de total flottait à
              gauche, décrochée de la colonne des montants qu'elle totalise. */}
          <div className="pied-facture">
            <div className="totaux">
              <div className="ligne-total gros">
                <span>Total rendu HT</span>
                {/* EN NÉGATIF À L'AFFICHAGE, parce que c'est ce que portera la
                  pièce. Montrer « 500,00 » là où le document dira « −500,00 »
                  ferait douter au moment de valider. */}
                <span className="code">
                  {total === 0 ? '0,00' : `−${saisieDite(total)}`}
                </span>
              </div>
            </div>
          </div>

          <div className="entete-facture">
            <label>
              <span className="etiquette">Motif — imprimé sur l’avoir</span>
              <input
                className="champ"
                type="text"
                maxLength={300}
                value={p.motif}
                disabled={p.occupe}
                placeholder="Erreur de tarif, prestation non réalisée…"
                onChange={(e) => {
                  p.quandMotif(e.target.value);
                }}
              />
            </label>
          </div>
        </>
      )}

      {p.refus !== null && (
        <div className="refus-pennylane">
          <p className="bandeau bandeau-refus">{p.refus}</p>
          {/* LE TEXTE DE L'API, TEL QUEL. Sur `avoir_trop_grand` il porte LE
              PLAFOND EN EUROS — sans lui, il faudrait tâtonner. */}
          {p.detail !== null && <p className="detail-pennylane">{p.detail}</p>}
        </div>
      )}

      <div className="actions-bas">
        <button
          className="bouton"
          type="button"
          disabled={p.occupe || p.chargement || retenues === 0 || motifVide}
          onClick={p.quandEtablir}
        >
          {p.occupe ? 'Création…' : 'Créer le brouillon d’avoir'}
        </button>
        {/* POURQUOI LE BOUTON EST ÉTEINT, DIT EN TOUTES LETTRES. Un bouton gris
            sans explication fait cliquer trois fois puis abandonner. */}
        {motifVide && !p.chargement && (
          <span className="maigre">
            Le motif est obligatoire : il s’imprime sur l’avoir.
          </span>
        )}
        {retenues === 0 && !p.chargement && !motifVide && (
          <span className="maigre">Aucune ligne retenue.</span>
        )}
        <div className="a-droite">
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={p.occupe}
            onClick={p.quandFermer}
          >
            Annuler
          </button>
        </div>
      </div>
    </>
  );
}

// ===========================================================================
// Vue — l'onglet « Factures »
// ===========================================================================

interface ProprietesFactures {
  readonly pieces: readonly PieceListee[];
  readonly aujourdhui: string;
  readonly du: string;
  readonly au: string;
  readonly etatFiltre: string;
  readonly envoiFiltre: string;
  readonly occupe: boolean;
  readonly quandDu: (v: string) => void;
  readonly quandAu: (v: string) => void;
  readonly quandEtat: (v: string) => void;
  readonly quandEnvoi: (v: string) => void;
  readonly quandOuvrir: (piece: PieceListee) => void;
  readonly quandEnvoyer: (piece: PieceListee, canal: string) => void;
  /** La pièce dont l'annulation est ARMÉE, en attente du second clic. */
  readonly devalideDemande: string | null;
  readonly quandDevalider: (piece: PieceListee) => void;
  /**
   * ÉTABLIR UN AVOIR — 11/09/2026. UN SEUL CLIC, contrairement à l'annulation
   * de validation : celui-ci n'écrit rien, il ouvre un écran où l'on choisit
   * les lignes et où l'on tape un motif. La confirmation, c'est cet écran.
   */
  readonly quandAvoir: (piece: PieceListee) => void;
  /**
   * CORRIGER LES DEUX DATES D'UNE PIÈCE TRANSCRITE — 16/09/2026. Voir
   * `BoiteDatesTranscrites` : ce geste n'existe que pour les pièces déclarées
   * déjà facturées ailleurs.
   */
  readonly quandCorrigerDates: (piece: PieceListee, dates: {
    datePiece: string;
    echeance: string;
  }) => void;
  readonly refusDates: string | null;
  readonly quandOublierRefusDates: () => void;
}

/** La classe de pastille qui va avec un état. */
function classeEtat(etat: string): string {
  if (etat === 'brouillon') return 'statut brouillon';
  if (etat === 'en_retard') return 'statut retard';
  if (etat === 'a_venir') return 'statut a-venir';
  // PAYÉE EST VERTE, comme « envoyée » : c'est le seul état qui dit qu'il n'y
  // a plus rien à faire. Ajouté le 10/09/2026 avec l'état lui-même.
  if (etat === 'payee') return 'statut envoye';
  return 'statut emise';
}

/**
 * CORRIGER LES DEUX DATES D'UNE PIÈCE TRANSCRITE — 16/09/2026.
 *
 * ELLE N'EXISTE QUE POUR LES PIÈCES DÉCLARÉES DÉJÀ FACTURÉES AILLEURS. Stafa
 * ne les a pas émises : il les transcrit, et une transcription se corrige pour
 * coller à l'original. Le document que le client a reçu vient de Pennylane et
 * ne bouge pas d'un pixel quoi qu'on fasse ici.
 *
 * CE QU'ELLE RÉPARE. Le premier jet de la déclaration ne demandait pas ces deux
 * dates : FA202609457 est entrée avec pièce au 16/09 et échéance au 16/10 là où
 * la vraie porte 15/09 et 14/10. Sans cette boîte, elle resterait fausse pour
 * toujours et le poste client relancerait deux jours trop tard.
 *
 * DEUX CHAMPS, ET PAS UN DE PLUS. Ni le numéro, ni les lignes, ni les montants.
 */
function BoiteDatesTranscrites(p: {
  readonly numero: string;
  readonly datePiece: string;
  readonly echeance: string;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: (dates: { datePiece: string; echeance: string }) => void;
}) {
  const [datePiece, setDatePiece] = useState(p.datePiece);
  const [echeance, setEcheance] = useState(p.echeance);
  const inversees = datePiece !== '' && echeance !== '' && echeance < datePiece;
  const complet = datePiece !== '' && echeance !== '' && !inversees;

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Dates de la pièce {p.numero}</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            p.quandEnregistrer({ datePiece, echeance });
          }}
        >
          <p className="quoi-decroche">
            Cette facture a été <b>déclarée déjà facturée ailleurs</b> : Stafa ne
            l’a pas émise, il la recopie. Ses deux dates se corrigent donc pour
            coller à la pièce d’origine. Le document reçu par le client ne change
            pas.
          </p>

          <label className="etiquette-boite" htmlFor="dates-piece">
            Date de la pièce
          </label>
          <input
            id="dates-piece"
            className="champ-boite"
            type="date"
            value={datePiece}
            disabled={p.envoi}
            onChange={(e) => setDatePiece(e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="dates-echeance">
            Échéance
          </label>
          <input
            id="dates-echeance"
            className="champ-boite"
            type="date"
            value={echeance}
            disabled={p.envoi}
            onChange={(e) => setEcheance(e.target.value)}
          />
          <p className="aide-boite">
            {inversees
              ? 'L’échéance ne peut pas précéder la date de la pièce.'
              : 'C’est l’échéance qui décide des relances du poste client.'}
          </p>

          {p.refus !== null && <p className="bandeau-refus">{p.refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={p.envoi || !complet}>
              {p.envoi ? 'Enregistrement…' : 'Corriger'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.envoi}
              onClick={p.quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function VueFactures(p: ProprietesFactures) {
  /** La pièce dont on corrige les dates, ou `null` : la boîte est fermée. */
  const [datesDe, setDatesDe] = useState<PieceListee | null>(null);

  return (
    <>
      {datesDe !== null && (
        <BoiteDatesTranscrites
          numero={datesDe.numero ?? '—'}
          datePiece={datesDe.date_piece ?? ''}
          echeance={datesDe.echeance ?? ''}
          envoi={p.occupe}
          refus={p.refusDates}
          quandFermer={() => {
            setDatesDe(null);
            p.quandOublierRefusDates();
          }}
          quandEnregistrer={(dates) => {
            p.quandCorrigerDates(datesDe, dates);
          }}
        />
      )}
      <div className="barre-outils">
        <label className="champ-date">
          <span className="etiquette">Du</span>
          <input
            className="champ"
            type="date"
            value={p.du}
            onChange={(e) => {
              p.quandDu(e.target.value);
            }}
          />
        </label>
        <label className="champ-date">
          <span className="etiquette">Au</span>
          <input
            className="champ"
            type="date"
            value={p.au}
            onChange={(e) => {
              p.quandAu(e.target.value);
            }}
          />
        </label>
        <label className="champ-date">
          <span className="etiquette">État</span>
          <select
            className="champ"
            value={p.etatFiltre}
            onChange={(e) => {
              p.quandEtat(e.target.value);
            }}
          >
            <option value="">Tous</option>
            <option value="brouillon">Brouillon</option>
            <option value="emise">Émise</option>
            <option value="a_venir">À venir</option>
            <option value="en_retard">En retard</option>
            <option value="payee">Payée</option>
          </select>
        </label>
        <label className="champ-date">
          <span className="etiquette">Envoi</span>
          <select
            className="champ"
            value={p.envoiFiltre}
            onChange={(e) => {
              p.quandEnvoi(e.target.value);
            }}
          >
            <option value="">Tous</option>
            <option value="pas-envoyee">Pas envoyée</option>
            {CANAUX.map((c) => (
              <option key={c.cle} value={c.cle}>
                {c.nom}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="decompte">{decompteFactures(p.pieces, p.aujourdhui)}</p>
      {/* DIT UNE FOIS, EN HAUT DE LA LISTE. Décision du 14/09/2026 :
          « pour le moment j'envoie à la main, le temps de mettre le mécanisme
          d'envoi en place ». Tant que c'est vrai, l'écran doit le dire —
          sinon la colonne Envoi se lit comme un bouton d'envoi. */}
      <p className="aide-saisie">
        Stafa n’envoie pas encore les factures : la colonne Envoi sert à
        <b> noter</b> un envoi fait à la main, avec son canal et sa date.
      </p>

      {p.pieces.length === 0 ? (
        <div className="vide">
          <p>Aucune pièce ne répond à ces critères.</p>
        </div>
      ) : (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col">Pièce</th>
                <th scope="col">Client</th>
                <th scope="col">Date</th>
                <th scope="col">Envoi</th>
                <th scope="col">Échéance</th>
                <th scope="col" className="n">
                  HT
                </th>
                <th scope="col" className="n">
                  TTC
                </th>
                <th scope="col">État</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {p.pieces.map((piece) => {
                const etat = etatFacture(piece, p.aujourdhui);
                const totaux = totauxDePiece(piece);
                const dit = pieceDite(piece);
                const retard =
                  etat === 'en_retard' && piece.echeance !== null
                    ? joursDeRetard(piece.echeance, p.aujourdhui)
                    : null;
                return (
                  <tr
                    key={piece.id}
                    className="rangee"
                    onClick={() => {
                      p.quandOuvrir(piece);
                    }}
                  >
                    <td className="code">
                      {dit.titre}
                      <span className="secondaire">{dit.sous}</span>
                    </td>
                    <td>{piece.client_nom}</td>
                    <td className="code">{dateDite(piece.date_piece)}</td>
                    <td>
                      {piece.envoye_le === null ? (
                        piece.emise_le === null ? (
                          <span className="statut pas-envoye">—</span>
                        ) : (
                          <select
                            className="champ menu-envoi"
                            value=""
                            disabled={p.occupe}
                            aria-label={`Noter l’envoi de ${dit.titre}`}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              p.quandEnvoyer(piece, e.target.value);
                            }}
                          >
                            <option value="">Pas envoyée</option>
                            {/* « NOTER », ET NON « ENVOYER » — repris le
                                14/09/2026, avant la mise en production.
                                Vérifié dans `apps/api/src/factures.ts` :
                                `POST /factures/:id/envoi` écrit `envoye_le` et
                                N'ENVOIE RIEN, son propre commentaire le dit.
                                Une liste de canaux sans ce mot se lisait comme
                                un ordre d'envoi ; en interne c'est un
                                pense-bête, devant un client c'est une facture
                                marquée partie que personne n'a reçue. */}
                            <optgroup label="Noter l’envoi par…">
                              {CANAUX.map((c) => (
                                <option key={c.cle} value={c.cle}>
                                  {c.nom}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        )
                      ) : (
                        <>
                          {/* « ENVOI NOTÉ » et non « Envoyée » : Stafa
                              n'envoie pas encore, et la pastille ne doit pas
                              affirmer le contraire. Le mot vaut pour une
                              facture comme pour un avoir — un accord de moins
                              à tenir. */}
                          <span className="statut envoye">Envoi noté</span>
                          <span className="secondaire">
                            {canalDit(piece.envoye_canal)} ·{' '}
                            {dateDite(piece.envoye_le.slice(0, 10))}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="code maigre">{dateDite(piece.echeance) ?? '—'}</td>
                    <td className="n code">{montant(totaux.htApresRemise)}</td>
                    <td className="n code">{montant(totaux.ttc)}</td>
                    <td>
                      <span className={classeEtat(etat)}>
                        {etatDit(etat, piece.type)}
                      </span>
                      {/* RATTACHÉE À LA MAIN — 16/09/2026. Une pièce déclarée
                          déjà facturée ailleurs n'est pas passée par la
                          validation, donc pas par les mêmes contrôles. Le dire
                          vaut mieux que de la faire ressembler aux autres :
                          celui qui la relira dans six mois doit savoir d'où
                          vient son numéro. */}
                      {(piece.emission_declaree_le ?? null) !== null && (
                        <span className="secondaire">Rattachée à la main</span>
                      )}
                      {retard !== null && (
                        <span className="secondaire">
                          {retard} jour{retard > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    {/* ANNULER LA VALIDATION — demandé le 10/09/2026 après une
                        validation par erreur. DEUX CLICS, comme la suppression
                        d'un brouillon : le geste rend un numéro légal, et un
                        bouton d'une seule pression dans une liste s'attrape
                        trop facilement.

                        L'API refuse si la pièce n'est pas la dernière de sa
                        série ou si un règlement s'y rattache. On n'essaie pas
                        de le deviner ici : c'est elle qui sait, et son refus
                        porte la suite à donner. */}
                    <td>
                      {/* DEPUIS LE 11/09/2026, UN SEUL DES DEUX BOUTONS, JAMAIS
                          LES DEUX.

                          Une pièce finalisée chez Pennylane NE SE DÉVALIDE
                          PLUS : on ne dé-finalise pas chez eux, et rendre le
                          numéro côté Stafa ferait diverger les deux
                          comptabilités. Le bouton « Annuler la validation »
                          échouerait à tous les coups — et un bouton qui ne
                          marche jamais est pire qu'un bouton absent, parce
                          qu'il fait croire que le geste existe.

                          IL RESTE POUR LES AUTRES, et elles existent : les
                          pièces émises AVANT le pivot, et les AVOIRS, qui
                          numérotent encore chez Stafa. */}
                      {/* CORRIGER LES DATES — 16/09/2026, et SEULEMENT sur une
                          pièce transcrite. Mohamed : « la date d'échéance, je
                          n'ai pas eu le choix ». Il avait raison : la première
                          version de la déclaration ne la demandait pas, et la
                          pièce est entrée avec les dates du brouillon. */}
                      {(piece.emission_declaree_le ?? null) !== null && (
                        <button
                          type="button"
                          className="bouton bouton-discret"
                          disabled={p.occupe}
                          onClick={(e) => {
                            e.stopPropagation();
                            p.quandOublierRefusDates();
                            setDatesDe(piece);
                          }}
                        >
                          Corriger les dates
                        </button>
                      )}
                      {/* UNE PIÈCE RATTACHÉE À LA MAIN PASSE PAR LA MÊME PORTE
                          QU'UNE PIÈCE FINALISÉE CHEZ EUX — 16/09/2026 : avoir,
                          et pas dévalidation. Son numéro ne vient pas de notre
                          série, et le rendre reculerait un compteur qu'elle
                          n'a jamais utilisé. L'API le refuse déjà ; ici on
                          évite d'offrir le bouton. */}
                      {piece.emise_le !== null &&
                        (piece.chez_pennylane === true ||
                        (piece.emission_declaree_le ?? null) !== null ? (
                          <button
                            type="button"
                            className="bouton bouton-risque"
                            disabled={p.occupe}
                            onClick={(e) => {
                              e.stopPropagation();
                              p.quandAvoir(piece);
                            }}
                          >
                            Établir un avoir
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="bouton bouton-risque"
                            disabled={p.occupe}
                            onClick={(e) => {
                              e.stopPropagation();
                              p.quandDevalider(piece);
                            }}
                          >
                            {p.devalideDemande === piece.id
                              ? 'Confirmer'
                              : 'Annuler la validation'}
                          </button>
                        ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// L'écran — celui qui décide
// ===========================================================================

/** Le jour, en ISO, tel que les comparaisons de dates l'attendent. */
function aujourdhuiIso(horloge: Date = new Date()): string {
  const deuxChiffres = (n: number): string => String(n).padStart(2, '0');
  return `${String(horloge.getFullYear())}-${deuxChiffres(horloge.getMonth() + 1)}-${deuxChiffres(
    horloge.getDate(),
  )}`;
}

/** Le 1er du mois en cours — la borne basse par défaut de la liste. */
function premierDuMois(jour: string): string {
  return `${jour.slice(0, 7)}-01`;
}

export interface ProprietesComptabilite {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /**
   * OUVRIR LA FICHE D'UN ORDRE depuis « À facturer » — 14/09/2026.
   *
   * L'écran ne sait pas y aller lui-même : la fiche d'un ordre vit dans
   * Exploitation. C'est l'application qui bascule, et qui retient le chemin du
   * retour — voir `ouvrirUnOrdre` dans `Application.tsx`.
   *
   * FACULTATIVE : `apercu.tsx` monte cet écran hors de l'application pour le
   * relire, et n'a nulle part où envoyer.
   */
  readonly quandOuvrirOrdre?: (otId: string) => void;
}

export function EcranComptabilite({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesComptabilite) {
  const aujourdhui = useMemo(() => aujourdhuiIso(), []);

  const [famille, setFamille] = useState<'facture' | 'recouvrement'>('facture');
  const [onglet, setOnglet] = useState<'a-facturer' | 'factures'>('a-facturer');
  const [panne, setPanne] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [ordres, setOrdres] = useState<readonly OrdreAFacturer[]>([]);
  const [retenus, setRetenus] = useState<ReadonlySet<string>>(new Set());
  const [replies, setReplies] = useState<ReadonlySet<string>>(new Set());
  /**
   * A-t-on déjà plié ou déplié à la main ?
   *
   * Sans cette garde, chaque relecture replierait tout et défairait ce que
   * l'utilisateur vient d'ouvrir. La sélection avait la même, sous le nom
   * `touche` ; elle est partie le 10/09 avec le cochage d'office.
   */
  const [pliTouche, setPliTouche] = useState(false);

  const [pieces, setPieces] = useState<readonly PieceListee[]>([]);
  /**
   * LE REFUS DE LA CORRECTION DES DATES — 16/09/2026. À part du bandeau
   * général : il se lit DANS la boîte, à côté des deux champs à corriger.
   */
  const [refusDates, setRefusDates] = useState<string | null>(null);
  const [du, setDu] = useState(premierDuMois(aujourdhui));
  const [au, setAu] = useState(aujourdhui);
  const [etatFiltre, setEtatFiltre] = useState('');
  const [envoiFiltre, setEnvoiFiltre] = useState('');

  /** La file d'édition : les brouillons à relire, et où on en est. */
  const [suite, setSuite] = useState<readonly string[] | null>(null);
  const [rang, setRang] = useState(0);
  const [ouverte, setOuverte] = useState<FactureOuverte | null>(null);
  /**
   * L'APERÇU PENNYLANE — 11/09/2026.
   *
   * `pdf` est une adresse `blob:` : elle occupe de la mémoire tant qu'on ne la
   * révoque pas. On la révoque donc à chaque nouvel aperçu et en fermant la
   * fenêtre — sinon une session de facturation en laisserait une par facture
   * regardée, et le navigateur s'alourdirait sans raison visible.
   */
  const [apercu, setApercu] = useState<ApercuPennylane>({
    etat: 'absent',
    pdf: null,
    refus: null,
    detail: null,
    alerte: false,
    ht: null,
    ttc: null,
  });
  /**
   * LES PIÈCES CANDIDATES de la facture ouverte — 14/09/2026.
   *
   * Relues à chaque ouverture, jamais reprises de la liste : la liste des
   * factures ne les porte pas, et une pièce peut être jointe à un ordre entre
   * deux ouvertures.
   */
  const [piecesAnnexes, setPiecesAnnexes] = useState<readonly PieceAnnexe[]>([]);
  /** Le premier clic sur « Supprimer ce brouillon ». Voir `quandSupprimer`. */
  const [supprimeDemande, setSupprimeDemande] = useState(false);
  /**
   * LA BOÎTE « DÉJÀ FACTURÉE CHEZ PENNYLANE » — 16/09/2026.
   *
   * SON REFUS EST À PART du bandeau général : il porte les deux totaux qui ne
   * concordent pas, et ils servent à corriger des champs qui sont dans la
   * boîte. Un message affiché derrière elle ne se lirait pas.
   */
  const [declaration, setDeclaration] = useState(false);
  const [refusDeclaration, setRefusDeclaration] = useState<string | null>(null);
  /** La pièce dont l'annulation de validation est armée. Voir `quandDevalider`. */
  const [devalideDemande, setDevalideDemande] = useState<string | null>(null);

  /**
   * L'AVOIR EN COURS DE PRÉPARATION — 11/09/2026.
   *
   * `piece` NON NULLE VEUT DIRE « la fenêtre est ouverte ». Un booléen à côté
   * de la pièce ferait deux états pour une seule réalité, et ils finiraient par
   * se contredire — un écran ouvert sur rien, ou une pièce fantôme.
   *
   * LES LIGNES SONT RELUES À L'OUVERTURE, jamais reprises de la liste : la
   * liste ne porte que des BASES PAR TAUX, pas les lignes. Et une facture peut
   * avoir changé depuis le chargement de la liste.
   */
  const [avoirPiece, setAvoirPiece] = useState<PieceListee | null>(null);
  const [avoirLignes, setAvoirLignes] = useState<readonly LigneFacture[]>([]);
  const [avoirChoix, setAvoirChoix] = useState<Record<string, ChoixAvoir>>({});
  const [avoirMotif, setAvoirMotif] = useState('');
  const [avoirChargement, setAvoirChargement] = useState(false);
  const [avoirRefus, setAvoirRefus] = useState<string | null>(null);
  const [avoirDetail, setAvoirDetail] = useState<string | null>(null);

  /** Referme la fenêtre ET oublie tout. Voir `oublierApercu` pour le pourquoi. */
  const fermerAvoir = useCallback(() => {
    setAvoirPiece(null);
    setAvoirLignes([]);
    setAvoirChoix({});
    setAvoirMotif('');
    setAvoirRefus(null);
    setAvoirDetail(null);
  }, []);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const groupes = useMemo(
    () => grouperParClient(ordres, aujourdhui),
    [ordres, aujourdhui],
  );

  /*
   * ON ARRIVE AVEC RIEN DE COCHÉ — demande du 10/09/2026.
   *
   * Il y avait ici un effet qui posait la sélection par défaut à chaque arrivée
   * des ordres. Il est retiré : `retenus` part vide et ne se remplit qu'au clic.
   * Le drapeau `touche`, qui n'existait que pour empêcher cet effet de
   * recocher ce que l'utilisateur venait de décocher, part avec lui. La
   * sélection est remise à vide aux trois endroits où la file se termine.
   */

  /**
   * LES CLIENTS ARRIVENT PLIÉS — demande de l'utilisateur du 09/09/2026 :
   * « dans les factures à émettre on laisse plier par défaut ».
   *
   * Ce qu'on lit d'abord, c'est QUI et COMBIEN : une ligne par client avec son
   * total. Le détail des ordres se déplie quand on doute d'un montant, ce qui
   * n'arrive pas à chaque fois. Déplié d'office, un jour de quarante ordres
   * donnait quarante lignes à faire défiler pour compter cinq clients.
   *
   * LA SÉLECTION NE CHANGE PAS pour autant : tout reste coché comme avant,
   * plier ne décoche rien.
   */
  useEffect(() => {
    if (!pliTouche) setReplies(new Set(groupes.map((g) => g.clientId)));
  }, [groupes, pliTouche]);

  const relireAFacturer = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lus = await lireLesOrdresAFacturer(j);
    if (lus.etat === 'ok') {
      setOrdres(lus.valeur);
      setPanne(false);
    } else {
      if (lus.cause === 'acces') quandAccesPerdu();
      setPanne(true);
    }
  }, [avecJeton, quandAccesPerdu]);

  /** Libère l'adresse `blob:` en cours, s'il y en a une. */
  const oublierApercu = useCallback((): void => {
    setApercu((a) => {
      if (a.pdf !== null) URL.revokeObjectURL(a.pdf);
      return {
        etat: 'absent',
        pdf: null,
        refus: null,
        detail: null,
        alerte: false,
        ht: null,
        ttc: null,
      };
    });
  }, []);

  /**
   * ENVOYER LA FACTURE CHEZ PENNYLANE, PUIS AFFICHER LEUR DOCUMENT.
   *
   * DEUX APPELS ET NON UN, et c'est voulu : le premier envoie et rend les
   * montants que PENNYLANE a calculés, le second rapporte le document. Les
   * fondre en un seul obligerait à faire passer les montants par des en-têtes,
   * ou à ne plus les montrer — alors qu'ils sont précisément ce qu'il faut
   * comparer avec le pied de la saisie.
   *
   * UN REFUS DU PREMIER APPEL ARRÊTE TOUT. Demander le document d'une pièce qui
   * n'est pas partie afficherait le document PRÉCÉDENT — un aperçu qui a l'air
   * juste et qui ne l'est pas, le pire des cas.
   */
  const apercuPennylane = useCallback(async () => {
    if (ouverte === null) return;
    const factureId = ouverte.facture.id;

    setApercu((a) => {
      if (a.pdf !== null) URL.revokeObjectURL(a.pdf);
      return {
        etat: 'en_cours',
        pdf: null,
        refus: null,
        detail: null,
        alerte: false,
        ht: null,
        ttc: null,
      };
    });

    const j = await avecJeton();
    if (j === null) return;

    const envoi = await envoyerAPennylane(j, factureId);
    if (envoi.etat !== 'ok') {
      if (envoi.cause === 'acces') quandAccesPerdu();
      setApercu({
        etat: 'refus',
        pdf: null,
        refus: REFUS[envoi.cause],
        // LE TEXTE DE L'API, tel quel : sur `pennylane_impossible`, il porte
        // souvent le message de Pennylane lui-même, et c'est la seule source
        // qui dise ce qui ne va pas.
        detail: envoi.detail ?? null,
        alerte: true,
        ht: null,
        ttc: null,
      });
      return;
    }

    /**
     * LES PIÈCES PARTENT DANS LA FOULÉE — lettres de voiture et bons de
     * livraison, 14/09/2026.
     *
     * ICI ET PAS AILLEURS, parce que c'est le seul moment où la facture vient
     * d'être créée chez eux : un second envoi SUPPRIME le brouillon précédent
     * et ses annexes avec lui. Déposer plus tard demanderait de savoir ce qui
     * est déjà parti, ce que personne ne retient.
     *
     * UN ÉCHEC DE DÉPÔT N'ARRÊTE PAS L'APERÇU. La facture est déjà chez eux :
     * afficher un refus ferait croire qu'elle n'est pas partie, et le geste de
     * réparation — renvoyer — supprimerait puis recréerait le brouillon pour
     * rien. On le DIT dans le bandeau, et on continue.
     */
    // SANS VALEUR DE DÉPART : les quatre branches ci-dessous écrivent toutes,
    // et il n'existe plus de cas où l'on ne dit rien. Un `= null` initial
    // laisserait croire le contraire.
    let motAnnexes: string;
    let annexesEnDefaut = false;
    const prevues = await lireAnnexesPrevues(j, factureId);
    if (prevues.etat !== 'ok') {
      // ON LE DIT PLUTÔT QUE DE SE TAIRE : sans cette phrase, une facture
      // partie SANS ses pièces ressemblerait trait pour trait à une facture
      // partie avec.
      motAnnexes =
        'Stafa n’a pas pu savoir quelles pièces devaient partir : vérifiez-les chez Pennylane.';
      annexesEnDefaut = true;
    } else if (prevues.valeur.length === 0) {
      /**
       * AUCUNE PIÈCE ÉLIGIBLE — ET ÇA SE DIT AUSSI.
       *
       * Le taire était un défaut, constaté le 14/09 au premier essai réel : le
       * volet ne montrait RIEN, et rien ne distingue « aucune pièce à joindre »
       * de « la fonction ne marche pas ». La phrase nomme les deux causes
       * possibles, qui sont les deux seules.
       */
      motAnnexes =
        'Aucune pièce à joindre : soit l’ordre n’en porte pas, soit leur type n’est pas coché « Joint à la facture » dans Paramètres.';
    } else {
      const depot = await deposerLesAnnexes(j, factureId, prevues.valeur);
      if (depot.etat !== 'ok') {
        motAnnexes =
          'Les pièces jointes n’ont pas pu être déposées. La facture, elle, est bien partie.';
        annexesEnDefaut = true;
      } else if (depot.valeur.deposees < depot.valeur.attendues) {
        const manquantes = depot.valeur.pieces
          .filter((piece) => !piece.depose)
          .map((piece) => piece.nom)
          .join(', ');
        motAnnexes = `${depot.valeur.deposees} pièce(s) jointe(s) sur ${depot.valeur.attendues}. Non déposée(s) : ${manquantes}.`;
        annexesEnDefaut = true;
      } else {
        motAnnexes = `${depot.valeur.deposees} pièce(s) jointe(s) à la facture.`;
      }
    }

    const doc = await lirePdfPennylane(j, factureId);
    if (doc.etat !== 'ok') {
      if (doc.cause === 'acces') quandAccesPerdu();
      setApercu({
        etat: 'refus',
        pdf: null,
        refus: REFUS[doc.cause],
        detail:
          'La facture est bien partie chez Pennylane, mais le document n’a pas pu être récupéré.',
        alerte: true,
        ht: envoi.valeur.ht,
        ttc: envoi.valeur.ttc,
      });
      return;
    }

    setApercu({
      etat: 'affiche',
      pdf: URL.createObjectURL(doc.valeur),
      refus: null,
      /**
       * UN AVERTISSEMENT QUI N'EST PAS UN REFUS. Le brouillon existe chez eux
       * mais Stafa ne l'a pas retenu : le prochain envoi ne saura pas le
       * supprimer, et il en restera deux. Le taire laisserait croire à un
       * envoi propre.
       */
      /**
       * DEUX CHOSES PEUVENT ÊTRE À DIRE, et l'avertissement passe devant : il
       * annonce un dégât (deux pièces chez Pennylane), là où le mot sur les
       * annexes est un compte rendu.
       */
      detail:
        [
          envoi.valeur.identifiant_garde
            ? null
            : 'Attention : Stafa n’a pas pu retenir cette pièce. Un second aperçu en créerait une de plus chez Pennylane.',
          motAnnexes,
        ]
          .filter((mot): mot is string => mot !== null)
          .join(' ') || null,
      alerte: !envoi.valeur.identifiant_garde || annexesEnDefaut,
      ht: envoi.valeur.ht,
      ttc: envoi.valeur.ttc,
    });
  }, [ouverte, avecJeton, quandAccesPerdu]);

  /** LOT 158 : combien de fois le bandeau Pennylane a fini une lecture. */
  const [copieLue, setCopieLue] = useState(0);
  const relireFactures = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lues = await lireLesFactures(j, { du, au });
    if (lues.etat === 'ok') {
      setPieces(lues.valeur);
      setPanne(false);
    } else {
      if (lues.cause === 'acces') quandAccesPerdu();
      setPanne(true);
    }
  }, [avecJeton, du, au, quandAccesPerdu]);

  useEffect(() => {
    if (famille !== 'facture' || suite !== null) return;
    if (onglet === 'a-facturer') void relireAFacturer();
    else void relireFactures();
  }, [famille, onglet, suite, relireAFacturer, relireFactures]);

  /**
   * Charger la facture du rang courant dès que la file avance.
   *
   * ET DÉSARMER LA SUPPRESSION. Sans cette ligne, armer la confirmation sur une
   * facture puis appuyer sur « Passer » ferait arriver la suivante avec le
   * bouton déjà sur « Confirmer la suppression » : un seul clic distrait
   * l'effacerait. La confirmation appartient à la facture affichée, pas à
   * l'écran.
   */
  useEffect(() => {
    setSupprimeDemande(false);
    // LES PIÈCES DE LA FACTURE PRÉCÉDENTE PARTENT AVEC ELLE. Les laisser
    // afficherait, le temps du chargement, les pièces d'une autre facture —
    // avec des cases à cocher qui écriraient sur la mauvaise.
    setPiecesAnnexes([]);
    if (suite === null) return;
    const id = suite[rang];
    if (id === undefined) return;
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lue = await lireUneFacture(j, id);
      if (abandonne) return;
      if (lue.etat === 'ok') setOuverte(lue.valeur);
      else {
        if (lue.cause === 'acces') quandAccesPerdu();
        setRefus(refusDit(lue.cause));
      }

      /**
       * LES PIÈCES, EN SECOND ET SANS BLOQUER. Un échec ici ne doit pas
       * empêcher d'éditer la facture : on n'affiche simplement pas le bloc, et
       * le volet le dira au moment de l'envoi — c'est là que ça compte.
       */
      const lues = await lireAnnexesPrevues(j, id);
      if (abandonne) return;
      if (lues.etat === 'ok') setPiecesAnnexes(lues.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [suite, rang, avecJeton, quandAccesPerdu]);

  /** Le filtre d'état et celui d'envoi se posent ICI : l'API ne les connaît pas. */
  const visibles = useMemo(
    () =>
      pieces.filter((piece) => {
        if (etatFiltre !== '' && etatFacture(piece, aujourdhui) !== etatFiltre)
          return false;
        if (envoiFiltre === '') return true;
        if (envoiFiltre === 'pas-envoyee') return piece.envoye_le === null;
        return piece.envoye_canal === envoiFiltre;
      }),
    [pieces, etatFiltre, envoiFiltre, aujourdhui],
  );

  /** Une écriture, son refus traduit, et la recharge de la facture ouverte. */
  const ecrire = useCallback(
    async (
      action: (
        j: string,
      ) => Promise<
        | { readonly etat: 'ok'; readonly valeur: FactureOuverte }
        | { readonly etat: 'echec'; readonly cause: EchecApi }
      >,
    ) => {
      const j = await avecJeton();
      if (j === null) return;
      setOccupe(true);
      setRefus(null);
      const fait = await action(j);
      setOccupe(false);
      if (fait.etat === 'ok') setOuverte(fait.valeur);
      else {
        if (fait.cause === 'acces') quandAccesPerdu();
        setRefus(refusDit(fait.cause));
      }
    },
    [avecJeton, quandAccesPerdu],
  );

  /** Passer à la facture suivante, ou sortir de la file si c'était la dernière. */
  const avancer = useCallback(() => {
    setRefus(null);
    setOuverte(null);
    oublierApercu();
    setRang((precedent) => {
      const total = suite?.length ?? 0;
      if (precedent + 1 >= total) {
        setSuite(null);
        setRetenus(new Set());
        setPliTouche(false);
        setOnglet('factures');
        return 0;
      }
      return precedent + 1;
    });
  }, [suite]);

  const facturer = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    setOccupe(true);
    setRefus(null);
    const faites = await facturerLesOrdres(j, [...retenus]);
    setOccupe(false);
    if (faites.etat !== 'ok') {
      if (faites.cause === 'acces') quandAccesPerdu();
      setRefus(refusDit(faites.cause));
      return;
    }
    if (faites.valeur.length === 0) {
      setRefus(refusDit('rien_a_facturer'));
      return;
    }
    setSuite(faites.valeur.map((f) => f.id));
    setRang(0);
  }, [avecJeton, retenus, quandAccesPerdu]);

  // -------------------------------------------------------------------------

  const barre = (
    <>
      {/* LOT 153 : la copie des factures Pennylane, relue à l'ouverture si elle
          a plus de quinze minutes. L'état « Payée » des factures en vient. */}
      <BandeauPennylane
        jeton={jeton}
        quandAccesPerdu={quandAccesPerdu}
        quandRelu={() => {
          void relireFactures();
          // LOT 158 : le Poste client et les Encaissements se relisent aussi.
          setCopieLue((n) => n + 1);
        }}
      />
      <div className="onglets-listes">
        {(
          [
            ['facture', 'Facture'],
            ['recouvrement', 'Recouvrement'],
          ] as const
        ).map(([cle, nom]) => (
          <button
            key={cle}
            type="button"
            className={famille === cle ? 'onglet-liste actif' : 'onglet-liste'}
            onClick={() => {
              setFamille(cle);
            }}
          >
            {nom}
          </button>
        ))}
      </div>
      {famille === 'facture' && (
        <div className="onglets-listes">
          {(
            [
              ['a-facturer', 'À facturer'],
              ['factures', 'Factures'],
            ] as const
          ).map(([cle, nom]) => (
            <button
              key={cle}
              type="button"
              className={onglet === cle ? 'onglet-liste actif' : 'onglet-liste'}
              onClick={() => {
                setOnglet(cle);
              }}
            >
              {nom}
            </button>
          ))}
        </div>
      )}
    </>
  );

  if (famille === 'recouvrement') {
    // LE RECOUVREMENT EST CONSTRUIT DEPUIS LE 10/09/2026. Il porte ses propres
    // onglets sous celui-ci — Suivi, Poste client, Relances, Encaissements.
    return (
      <div className="corps corps-ot">
        <section className="liste">
          {barre}
          <EcranRecouvrement jeton={jeton} quandAccesPerdu={quandAccesPerdu} version={copieLue} />
        </section>
      </div>
    );
  }

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {barre}
        {refus !== null && <p className="bandeau-refus">{refus}</p>}
        {panne && suite === null && (
          <p className="bandeau-refus">
            La comptabilité n’a pas pu être lue. Réessayez dans un instant.
          </p>
        )}

        {onglet === 'a-facturer' ? (
          <VueAFacturer
            groupes={groupes}
            retenus={retenus}
            replies={replies}
            occupe={occupe}
            quandOuvrirOrdre={quandOuvrirOrdre}
            quandOrdre={(id) => {
              setRetenus((avant) => {
                const apres = new Set(avant);
                if (apres.has(id)) apres.delete(id);
                else apres.add(id);
                return apres;
              });
            }}
            quandClient={(groupe) => {
              setRetenus((avant) => {
                const apres = new Set(avant);
                const tous = groupe.ordres.every((o) => apres.has(o.id));
                for (const o of groupe.ordres) {
                  if (tous) apres.delete(o.id);
                  else apres.add(o.id);
                }
                return apres;
              });
            }}
            quandPlier={(clientId) => {
              setPliTouche(true);
              setReplies((avant) => {
                const apres = new Set(avant);
                if (apres.has(clientId)) apres.delete(clientId);
                else apres.add(clientId);
                return apres;
              });
            }}
            quandFacturer={() => {
              void facturer();
            }}
          />
        ) : (
          <VueFactures
            refusDates={refusDates}
            quandOublierRefusDates={() => {
              setRefusDates(null);
            }}
            quandCorrigerDates={(piece, dates) => {
              void (async () => {
                const j = await avecJeton();
                if (j === null) return;
                setOccupe(true);
                setRefusDates(null);
                const faite = await corrigerLesDatesTranscrites(j, piece.id, dates);
                setOccupe(false);
                if (faite.etat === 'ok') {
                  // LA LISTE SE RELIT : les deux dates et l'état « en retard »
                  // en dependent, et les recalculer a la main ici serait un
                  // second calcul a tenir d'accord avec celui de l'API.
                  await relireFactures();
                  return;
                }
                if (faite.cause === 'acces') quandAccesPerdu();
                setRefusDates(faite.message ?? refusDit(faite.cause));
              })();
            }}
            pieces={visibles}
            aujourdhui={aujourdhui}
            du={du}
            au={au}
            etatFiltre={etatFiltre}
            envoiFiltre={envoiFiltre}
            occupe={occupe}
            quandDu={setDu}
            quandAu={setAu}
            quandEtat={setEtatFiltre}
            quandEnvoi={setEnvoiFiltre}
            quandOuvrir={(piece) => {
              // ON N'OUVRE QUE LES BROUILLONS. Une pièce émise ne se modifie
              // plus ; l'ouvrir dans la page d'édition ferait croire l'inverse.
              if (piece.emise_le !== null) return;
              setSuite([piece.id]);
              setRang(0);
            }}
            devalideDemande={devalideDemande}
            quandAvoir={(piece) => {
              /**
               * ON OUVRE LA FENÊTRE ET ON RELIT LA FACTURE. Rien n'est écrit
               * ici : ce clic n'engage à rien, c'est l'écran d'après qui
               * engage.
               *
               * TOUTES LES LIGNES SONT COCHÉES À L'OUVERTURE, au montant
               * d'origine. C'est le cas courant — on annule une facture entière
               * bien plus souvent qu'une ligne sur trois — et décocher est plus
               * rapide que cocher.
               */
              setAvoirPiece(piece);
              setAvoirLignes([]);
              setAvoirChoix({});
              setAvoirMotif('');
              setAvoirRefus(null);
              setAvoirDetail(null);
              setAvoirChargement(true);
              void (async () => {
                const j = await avecJeton();
                if (j === null) {
                  setAvoirChargement(false);
                  return;
                }
                const lue = await lireUneFacture(j, piece.id);
                setAvoirChargement(false);
                if (lue.etat !== 'ok') {
                  if (lue.cause === 'acces') quandAccesPerdu();
                  setAvoirRefus(refusDit(lue.cause));
                  return;
                }
                setAvoirLignes(lue.valeur.lignes);
                const depart: Record<string, ChoixAvoir> = {};
                for (const l of lue.valeur.lignes) {
                  // EN FRANÇAIS, comme les champs de l'éditeur. Vu au rendu :
                  // le champ affichait « 270.00 » à côté d'une colonne qui
                  // disait « 270,00 ». Deux écritures du même nombre côte à
                  // côte, sur un écran où l'on rend de l'argent.
                  depart[l.id] = { prise: true, prix: saisieDite(l.prix_unitaire) };
                }
                setAvoirChoix(depart);
              })();
            }}
            quandDevalider={(piece) => {
              // PREMIER CLIC : on arme, on n'écrit rien.
              if (devalideDemande !== piece.id) {
                setDevalideDemande(piece.id);
                return;
              }
              void (async () => {
                const j = await avecJeton();
                if (j === null) return;
                setOccupe(true);
                setRefus(null);
                const fait = await annulerLaValidation(j, piece.id);
                setOccupe(false);
                setDevalideDemande(null);
                if (fait.etat === 'ok') void relireFactures();
                else {
                  if (fait.cause === 'acces') quandAccesPerdu();
                  setRefus(refusDit(fait.cause));
                }
              })();
            }}
            quandEnvoyer={(piece, canal) => {
              if (canal === '') return;
              void (async () => {
                const j = await avecJeton();
                if (j === null) return;
                setOccupe(true);
                setRefus(null);
                const fait = await enregistrerLEnvoi(j, piece.id, canal);
                setOccupe(false);
                if (fait.etat === 'ok') void relireFactures();
                else {
                  if (fait.cause === 'acces') quandAccesPerdu();
                  setRefus(refusDit(fait.cause));
                }
              })();
            }}
          />
        )}
      </section>

      {/* L'ÉDITION EST UNE FENÊTRE, depuis le 09/09/2026.

          Constat de l'utilisateur : « ce n'est pas visuel ». L'édition
          remplaçait le tableau dans le même cadre, au même endroit, à la même
          largeur — rien ne disait qu'on avait changé de mode.

          Elle se pose maintenant PAR-DESSUS la liste, qui reste visible de
          chaque côté. On voit d'où l'on vient, et on sait qu'on est ailleurs.
          Voir `.voile-facture` dans `styles.css` pour le pourquoi des
          dimensions. */}
      {/* LA FENÊTRE D'AVOIR — 11/09/2026. Le même voile que l'édition, et pour
          la même raison : on voit d'où l'on vient, et on sait qu'on est
          ailleurs. Elle ne coexiste jamais avec l'éditeur — on y entre depuis
          la LISTE, où l'éditeur est fermé. */}
      {avoirPiece !== null && (
        <div className="voile-facture" role="presentation">
          <div
            className="fenetre-facture"
            role="dialog"
            aria-modal="true"
            aria-label={`Établir un avoir sur ${avoirPiece.numero ?? 'cette pièce'}`}
          >
            <VueAvoir
              piece={avoirPiece}
              lignes={avoirLignes}
              choix={avoirChoix}
              motif={avoirMotif}
              occupe={occupe}
              chargement={avoirChargement}
              refus={avoirRefus}
              detail={avoirDetail}
              quandMotif={setAvoirMotif}
              quandPrise={(ligneId, prise) => {
                setAvoirChoix((avant) => {
                  const c = avant[ligneId];
                  if (c === undefined) return avant;
                  return { ...avant, [ligneId]: { ...c, prise } };
                });
              }}
              quandPrix={(ligneId, prix) => {
                setAvoirChoix((avant) => {
                  const c = avant[ligneId];
                  if (c === undefined) return avant;
                  return { ...avant, [ligneId]: { ...c, prix } };
                });
              }}
              quandFermer={fermerAvoir}
              quandEtablir={() => {
                void (async () => {
                  const j = await avecJeton();
                  if (j === null) return;
                  setOccupe(true);
                  setAvoirRefus(null);
                  setAvoirDetail(null);

                  /**
                   * ON N'ENVOIE `lignes` QUE SI L'AVOIR EST PARTIEL.
                   *
                   * Sur un avoir TOTAL au montant d'origine, l'absence du champ
                   * dit « tout, tel quel » — et c'est plus juste que d'énumérer
                   * : si la facture gagnait une ligne entre l'ouverture de cet
                   * écran et le clic, une liste figée l'oublierait en silence.
                   */
                  const prises = avoirLignes.filter(
                    (l) => avoirChoix[l.id]?.prise === true,
                  );
                  const intactes = prises.every(
                    (l) =>
                      Number(saisieBrute(avoirChoix[l.id]?.prix ?? '0')) ===
                      Number(l.prix_unitaire),
                  );
                  const total = prises.length === avoirLignes.length && intactes;
                  const detail: LigneAAvoirer[] = prises.map((l) => ({
                    id: l.id,
                    // LE POINT DÉCIMAL, quoi qu'on ait tapé. `numeric` de
                    // PostgreSQL ne connaît pas la virgule, et le refus
                    // sortirait en « données invalides » sans dire laquelle.
                    // `saisieBrute` ET NON UN `replace` À LA MAIN : elle
                    // retire aussi les séparateurs de milliers, que l'API
                    // refuse. Un champ affiché « 1 240,00 » et réenvoyé tel
                    // quel serait rejeté sans qu'on ait rien touché.
                    prix_unitaire: saisieBrute(avoirChoix[l.id]?.prix ?? '0'),
                  }));

                  const fait = await etablirUnAvoir(
                    j,
                    avoirPiece.id,
                    avoirMotif.trim(),
                    total ? undefined : detail,
                  );
                  setOccupe(false);
                  if (fait.etat !== 'ok') {
                    if (fait.cause === 'acces') quandAccesPerdu();
                    setAvoirRefus(refusDit(fait.cause));
                    // LE TEXTE DE L'API : sur `avoir_trop_grand` il porte le
                    // plafond en euros, qu'aucune phrase générique ne dirait.
                    setAvoirDetail(fait.detail ?? null);
                    return;
                  }

                  /**
                   * ON ENCHAÎNE SUR L'ÉDITEUR, avec l'avoir qui vient de
                   * naître. C'est la vue 3 de la maquette : on relit son
                   * document chez Pennylane, puis on valide — ou on corrige,
                   * tant qu'il est brouillon.
                   *
                   * Sans cet enchaînement, l'avoir tomberait dans la liste et
                   * il faudrait aller le rechercher. Un brouillon qu'on oublie
                   * de valider est un client qu'on ne rembourse jamais.
                   */
                  fermerAvoir();
                  void relireFactures();
                  setSuite([fait.valeur.facture.id]);
                  setRang(0);
                })();
              }}
            />
          </div>
        </div>
      )}

      {suite !== null && ouverte !== null && (
        <div className="voile-facture" role="presentation">
          {/* `fenetre-large` : SEULE la fenêtre d'édition s'élargit. Elle
              porte deux volets depuis le 14/09/2026 ; l'avoir, le gel et
              l'encaissement partagent la même classe `fenetre-facture` et
              n'ont rien demandé. */}
          <div
            className="fenetre-facture fenetre-large"
            role="dialog"
            aria-modal="true"
            aria-label={`Édition de la facture ${String(rang + 1)} sur ${String(suite.length)}`}
          >
            <VueEdition
              ouverte={ouverte}
              apercu={apercu}
              quandApercu={() => void apercuPennylane()}
              pieces={piecesAnnexes}
              /**
               * ON N'INVERSE PAS LA CASE LOCALEMENT EN ATTENDANT L'API : la
               * liste rendue par la route EST la nouvelle vérité. Poser la
               * valeur à l'avance puis la corriger ferait clignoter la case, et
               * mentirait pendant un instant si l'écriture échouait.
               */
              quandBasculerPiece={(piece) => {
                void (async () => {
                  const j = await avecJeton();
                  if (j === null) return;
                  setOccupe(true);
                  setRefus(null);
                  const fait = await changerPieceAnnexe(
                    j,
                    ouverte.facture.id,
                    piece.id,
                    !piece.partira,
                  );
                  setOccupe(false);
                  if (fait.etat === 'ok') setPiecesAnnexes(fait.valeur);
                  else {
                    if (fait.cause === 'acces') quandAccesPerdu();
                    setRefus(refusDit(fait.cause));
                  }
                })();
              }}
              rang={rang + 1}
              combien={suite.length}
              occupe={occupe}
              quandEntete={(champ, valeur) => {
                void ecrire((j) =>
                  modifierUneFacture(
                    j,
                    ouverte.facture.id,
                    valeur === '' ? { [champ]: null } : { [champ]: valeur },
                  ),
                );
              }}
              quandLigne={(ligne, champ, valeur) => {
                void ecrire((j) =>
                  modifierUneLigne(j, ouverte.facture.id, ligne.id, {
                    [champ]: valeur,
                  }),
                );
              }}
              quandRetirerLigne={(ligne) => {
                void ecrire((j) => supprimerUneLigne(j, ouverte.facture.id, ligne.id));
              }}
              quandAjouterLigne={(categorie) => {
                void ecrire((j) =>
                  ajouterUneLigne(j, ouverte.facture.id, {
                    // LE LIBELLÉ DE LA SURCHARGE EST FIGÉ — décision du 10/09.
                    // Il est écrit SUR la ligne à sa création, et non calculé
                    // à l'impression : si le libellé change un jour, les
                    // factures déjà émises garderont le leur.
                    designation:
                      categorie === 'surcharge'
                        ? LIBELLE_SURCHARGE
                        : 'Prestation complémentaire',
                    quantite: '1',
                    prix_unitaire: '0',
                    categorie,
                  }),
                );
              }}
              quandValider={() => {
                void (async () => {
                  const j = await avecJeton();
                  if (j === null) return;
                  setOccupe(true);
                  setRefus(null);
                  const faite = await validerUneFacture(j, ouverte.facture.id);
                  setOccupe(false);
                  if (faite.etat === 'ok') avancer();
                  else {
                    if (faite.cause === 'acces') quandAccesPerdu();
                    setRefus(refusDit(faite.cause));
                  }
                })();
              }}
              quandBrouillon={avancer}
              quandPasser={avancer}
              declarationOuverte={declaration}
              refusDeclaration={refusDeclaration}
              quandOuvrirDeclaration={() => {
                setRefusDeclaration(null);
                setDeclaration(true);
              }}
              quandFermerDeclaration={() => {
                setDeclaration(false);
                setRefusDeclaration(null);
              }}
              quandDeclarer={(saisie) => {
                void (async () => {
                  const j = await avecJeton();
                  if (j === null) return;
                  setOccupe(true);
                  setRefusDeclaration(null);
                  const faite = await declarerDejaFacturee(
                    j,
                    ouverte.facture.id,
                    saisie,
                  );
                  setOccupe(false);
                  if (faite.etat === 'ok') {
                    setDeclaration(false);
                    avancer();
                    return;
                  }
                  if (faite.cause === 'acces') quandAccesPerdu();
                  /**
                   * LE REFUS SE LIT DANS LA BOÎTE, pas dans le bandeau
                   * général : les deux montants qu'il porte servent à corriger
                   * les champs qui sont juste au-dessus. Le message de l'API
                   * est préféré à celui du code quand il existe — lui seul
                   * porte les chiffres.
                   */
                  setRefusDeclaration(faite.message ?? refusDit(faite.cause));
                })();
              }}
              supprimeDemande={supprimeDemande}
              quandSupprimer={() => {
                // PREMIER CLIC : on arme, on n'écrit rien.
                if (!supprimeDemande) {
                  setSupprimeDemande(true);
                  return;
                }
                void (async () => {
                  const j = await avecJeton();
                  if (j === null) return;
                  setOccupe(true);
                  setRefus(null);
                  const faite = await supprimerUneFacture(j, ouverte.facture.id);
                  setOccupe(false);
                  setSupprimeDemande(false);
                  if (faite.etat === 'ok') {
                    // LA FILE RÉTRÉCIT. Sans ça, `avancer` compterait encore la
                    // facture qu'on vient d'effacer et l'écran essaierait de la
                    // relire — 404.
                    const restantes = suite.filter((id) => id !== ouverte.facture.id);
                    setOuverte(null);
                    oublierApercu();
                    if (restantes.length === 0) {
                      setSuite(null);
                      setRang(0);
                      setRetenus(new Set());
                      setPliTouche(false);
                      setPliTouche(false);
                      setOnglet('a-facturer');
                    } else {
                      setSuite(restantes);
                      setRang(Math.min(rang, restantes.length - 1));
                    }
                    return;
                  }
                  if (faite.cause === 'acces') quandAccesPerdu();
                  setRefus(refusDit(faite.cause));
                })();
              }}
              quandAbandonner={() => {
                // « TOUT ABANDONNER » NE DÉFAIT PAS CE QUI EST VALIDÉ — on ne
                // reprend pas un numéro attribué. Il quitte la file ; les
                // brouillons restants restent des brouillons, visibles dans
                // l'onglet Factures.
                setSuite(null);
                setRang(0);
                setOuverte(null);
                oublierApercu();
                setRetenus(new Set());
                setPliTouche(false);
                setPliTouche(false);
                setOnglet('factures');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
